import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import ruLocale from "@fullcalendar/core/locales/ru";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../../auth/authStore";
import { useWs } from "../../ws/wsStore";
import { sessionsApi } from "../../api/sessionsApi";
import { paymentsApi } from "../../api/paymentsApi";
import Modal from "../ui/Modal";
import AuthRequiredModal from "../ui/AuthRequiredModal";
import { useToast } from "../../ui/toast/ToastProvider";
import WeeklyScheduleEditor from "./WeeklyScheduleEditor";
import FlexibleScheduleEditor from "./FlexibleScheduleEditor";
import {
    isBookingTemporarilyDisabled,
    triggerMaintenanceBanner,
} from "../../utils/maintenanceBanner";
import {
    addMinutes,
    buildBreakEvents,
    buildSlotEvents,
    dateToYmd,
    normalizeBookingToEvent,
    normalizeDayOffToEvent,
} from "./calendarUtils";

// ✅ правило: записываться можно только если до старта >= 30 минут
const MIN_BOOK_AHEAD_MIN = 30;
const MIN_BOOK_AHEAD_MS = MIN_BOOK_AHEAD_MIN * 60 * 1000;

// ✅ таймер удержания брони до оплаты
const HOLD_PAYMENT_MIN = 15;
const HOLD_PAYMENT_MS = HOLD_PAYMENT_MIN * 60 * 1000;
const PAYMENT_STATUS_REFRESH_INTERVAL_MS = 30_000;
const PAYMENT_STATUS_REFRESH_BATCH = 20;

// ✅ защита от массового «протыкания» слотов без оплаты (только фронт)
const HOLD_LIMIT_PER_HOUR = 3;
const HOLD_LIMIT_WINDOW_MS = 60 * 60 * 1000;

/* -------------------- helpers -------------------- */

function pad2(n) {
    return String(n).padStart(2, "0");
}
function formatMMSS(ms) {
    const safeMs = Math.max(0, Number(ms) || 0);
    const totalSec = Math.ceil(safeMs / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${pad2(m)}:${pad2(s)}`;
}

function isBlockingBookingStatus(status) {
    const s = String(status || "");
    // ✅ OPEN и CANCELLED НЕ блокируют доступность
    return s !== "OPEN" && s !== "CANCELLED";
}

function isVisibleBookingStatusInCalendar(status) {
    const s = String(status || "");
    // ✅ чтобы OPEN/CANCELLED не перекрывали слоты (и не создавали дубль-ивенты)
    return s !== "OPEN" && s !== "CANCELLED";
}

function slotBookable(startDate, nowDate = new Date()) {
    if (!startDate) return false;
    const diff = startDate.getTime() - nowDate.getTime();
    return diff >= MIN_BOOK_AHEAD_MS;
}

function getClientIdFromBooking(b) {
    return (
        b?.clientId ??
        b?.client_id ??
        b?.client?.id ??
        b?.client?.userId ??
        b?.client?.user_id ??
        null
    );
}

// статус брони — красиво
const BOOKING_STATUS_LABELS = {
    PENDING_PAYMENT: "Ожидает оплаты",
    PAID: "Оплачено",
    CANCELLED: "Отменено",
    COMPLETED: "Завершено",
    NO_SHOW: "Неявка",
    OPEN: "Открыто",
};

function prettyBookingStatus(status) {
    const key = String(status || "").toUpperCase();
    return BOOKING_STATUS_LABELS[key] || key || "—";
}

// какие статусы считаем "ожидание оплаты"
function isAwaitingPaymentStatus(status) {
    const s = String(status || "").toUpperCase();
    return s === "PENDING_PAYMENT" || s === "PENDING";
}

function isPaidBookingStatus(status) {
    const s = String(status || "").toUpperCase();
    return (
        s === "PAID" ||
        s === "CONFIRMED" ||
        s === "SUCCEEDED" ||
        s === "APPROVED" ||
        s === "SUCCESS" ||
        s === "CAPTURED"
    );
}

function isFailedPaymentStatus(status) {
    const s = normalizeToken(status);
    return s === "CANCELLED" || s === "FAILED" || s === "ERROR" || s === "REFUNDED";
}

function normalizeToken(value) {
    return String(value || "").trim().toUpperCase();
}

function readBookingPaymentStatus(booking) {
    const source = booking && typeof booking === "object" ? booking : {};
    const nested = source?.payment && typeof source.payment === "object" ? source.payment : {};
    const raw =
        source?.paymentStatus ??
        source?.payment_status ??
        source?.paymentState ??
        source?.payment_state ??
        source?.lastPaymentStatus ??
        source?.last_payment_status ??
        nested?.status ??
        nested?.state ??
        null;
    return normalizeToken(raw);
}

function isBookingConsideredPaid(booking) {
    if (!booking || typeof booking !== "object") return false;
    if (booking?.paid === true) return true;
    if (booking?.telemostUrl || booking?.joinUrl || booking?.roomToken || booking?.sessionRoomToken) return true;
    if (isPaidBookingStatus(booking?.status)) return true;
    return isPaidBookingStatus(readBookingPaymentStatus(booking));
}

function normalizeBookingForUi(booking) {
    if (!booking || typeof booking !== "object") return booking;
    const paymentStatus = readBookingPaymentStatus(booking);
    if (isBookingConsideredPaid(booking) && !isPaidBookingStatus(booking?.status)) {
        return { ...booking, status: "PAID", paymentStatus: paymentStatus || booking?.paymentStatus || null };
    }
    return paymentStatus ? { ...booking, paymentStatus } : booking;
}

// ---- remember own bookings / hold / payment-started ----

const HOLD_ATTEMPTS_KEY = (userId) => `bs:holdAttempts:${String(userId || "anon")}`;

function readHoldAttempts(userId) {
    try {
        const raw = localStorage.getItem(HOLD_ATTEMPTS_KEY(userId));
        const arr = JSON.parse(raw || "[]");
        if (!Array.isArray(arr)) return [];
        const now = Date.now();
        return arr
            .map((x) => ({ ts: Number(x?.ts), bookingId: x?.bookingId != null ? String(x.bookingId) : null }))
            .filter((x) => Number.isFinite(x.ts) && now - x.ts <= HOLD_LIMIT_WINDOW_MS);
    } catch {
        return [];
    }
}

function writeHoldAttempts(userId, arr) {
    try {
        localStorage.setItem(HOLD_ATTEMPTS_KEY(userId), JSON.stringify(arr || []));
    } catch {
        // ignore
    }
}

function addHoldAttempt(userId, bookingId) {
    const arr = readHoldAttempts(userId);
    arr.push({ ts: Date.now(), bookingId: bookingId != null ? String(bookingId) : null });
    writeHoldAttempts(userId, arr);
}

function removeHoldAttempt(userId, bookingId) {
    if (!bookingId) return;
    const arr = readHoldAttempts(userId).filter((x) => String(x.bookingId) !== String(bookingId));
    writeHoldAttempts(userId, arr);
}

function countHoldAttemptsLastHour(userId) {
    return readHoldAttempts(userId).length;
}
const OWN_BOOKING_KEY = (bookingId) => `bs:ownBooking:${String(bookingId)}`;
const HOLD_KEY = (bookingId) => `bs:bookingHoldExpiresAt:${String(bookingId)}`;
const PAYMENT_STARTED_KEY = (bookingId) => `bs:bookingPaymentStarted:${String(bookingId)}`;

function rememberOwnBooking(bookingId, userId) {
    try {
        if (!bookingId || !userId) return;
        localStorage.setItem(OWN_BOOKING_KEY(bookingId), String(userId));
    } catch {}
}

function isRememberedOwnBooking(bookingId, userId) {
    try {
        if (!bookingId || !userId) return false;
        return localStorage.getItem(OWN_BOOKING_KEY(bookingId)) === String(userId);
    } catch {
        return false;
    }
}

function rememberHoldExpiresAt(bookingId, expiresAt) {
    try {
        if (!bookingId || !expiresAt) return;
        localStorage.setItem(HOLD_KEY(bookingId), String(expiresAt));
    } catch {}
}

function getRememberedHoldExpiresAt(bookingId) {
    try {
        const raw = localStorage.getItem(HOLD_KEY(bookingId));
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
}

function clearRememberedHoldExpiresAt(bookingId) {
    try {
        localStorage.removeItem(HOLD_KEY(bookingId));
    } catch {}
}

function rememberPaymentStarted(bookingId) {
    try {
        if (!bookingId) return;
        localStorage.setItem(PAYMENT_STARTED_KEY(bookingId), "1");
    } catch {}
}

function isRememberedPaymentStarted(bookingId) {
    try {
        if (!bookingId) return false;
        return localStorage.getItem(PAYMENT_STARTED_KEY(bookingId)) === "1";
    } catch {
        return false;
    }
}

function clearRememberedPaymentStarted(bookingId) {
    try {
        localStorage.removeItem(PAYMENT_STARTED_KEY(bookingId));
    } catch {}
}

// чистим query после возврата
function clearPaymentQueryParams() {
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete("payment");
        url.searchParams.delete("bookingId");
        url.searchParams.delete("mock");
        url.searchParams.delete("paid");
        window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    } catch {}
}

function buildPaymentReturnUrl(bookingId) {
    try {
        const url = new URL(window.location.href);
        url.searchParams.set("payment", "return");
        url.searchParams.set("bookingId", String(bookingId));
        return url.toString();
    } catch {
        return `${window.location.origin}/?payment=return&bookingId=${encodeURIComponent(
            String(bookingId)
        )}`;
    }
}

const MY_APPOINTMENTS_ROUTE = "/my-appointments";

function normalizePaymentStatusResponse(res) {
    // ожидаем { bookingId, paymentStatus, bookingStatus }
    // но делаем максимально устойчиво
    const paymentStatus =
        res?.paymentStatus || res?.payment_status || res?.status || null;

    const bookingStatus =
        res?.bookingStatus || res?.booking_status || res?.bookingState || res?.booking_state || null;

    return {
        paymentStatus: paymentStatus ? String(paymentStatus).toUpperCase() : null,
        bookingStatus: bookingStatus ? String(bookingStatus).toUpperCase() : null,
    };
}

const MINI_WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MINI_MONTHS = [
    "янв",
    "фев",
    "мар",
    "апр",
    "май",
    "июн",
    "июл",
    "авг",
    "сен",
    "окт",
    "ноя",
    "дек",
];
const WEEKDAY_NAMES = {
    1: "Понедельник",
    2: "Вторник",
    3: "Среда",
    4: "Четверг",
    5: "Пятница",
    6: "Суббота",
    7: "Воскресенье",
};
const WEEKDAY_SHORT_NAMES = {
    1: "Пн",
    2: "Вт",
    3: "Ср",
    4: "Чт",
    5: "Пт",
    6: "Сб",
    7: "Вс",
};
const QUICK_SLOT_DURATIONS = [30, 50, 60, 80, 90];
const QUICK_SLOT_TYPES = {
    FREE: "FREE",
    BREAK: "BREAK",
    BUSY: "BUSY",
    DAY_OFF: "DAY_OFF",
};

function startOfDay(dateLike) {
    const d = new Date(dateLike);
    d.setHours(0, 0, 0, 0);
    return d;
}

function addDays(dateLike, days) {
    const d = new Date(dateLike);
    d.setDate(d.getDate() + days);
    return d;
}

function monthLabelRu(dateLike) {
    const d = new Date(dateLike);
    return `${MINI_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function buildMiniMonthGrid(anchorDate) {
    const monthStart = startOfDay(new Date(anchorDate.getFullYear(), anchorDate.getMonth(), 1));
    const mondayShift = (monthStart.getDay() + 6) % 7;
    const gridStart = addDays(monthStart, -mondayShift);

    return Array.from({ length: 42 }).map((_, idx) => {
        const date = addDays(gridStart, idx);
        return {
            date,
            ymd: dateToYmd(date),
            dayNum: date.getDate(),
            isOtherMonth: date.getMonth() !== anchorDate.getMonth(),
        };
    });
}

function initialsFromName(value) {
    const text = String(value || "").trim();
    if (!text) return "??";
    const parts = text.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function compactIntervalText(intervals) {
    if (!Array.isArray(intervals) || !intervals.length) return "Выходной";
    const mapped = intervals
        .map((it) => {
            const from = String(it?.start || "").slice(0, 5);
            const to = String(it?.end || "").slice(0, 5);
            if (!from || !to) return null;
            return `${from}–${to}`;
        })
        .filter(Boolean);

    if (!mapped.length) return "Выходной";
    if (mapped.length === 1) return mapped[0];
    return `${mapped[0]} +${mapped.length - 1}`;
}

function cloneDeep(value) {
    if (value == null) return value;
    try {
        return JSON.parse(JSON.stringify(value));
    } catch {
        return value;
    }
}

function makeEmptyWeek() {
    return { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
}

function ensureScheduleShape(value) {
    const src = value && typeof value === "object" ? value : {};
    const week = src.week && typeof src.week === "object" ? src.week : {};
    return {
        slotMinutes: Number(src.slotMinutes) > 0 ? Number(src.slotMinutes) : 50,
        bufferMinutes: 0,
        week: {
            ...makeEmptyWeek(),
            ...week,
        },
    };
}

function ensureFlexibleShape(value, fallbackSlotMinutes = 50) {
    const src = value && typeof value === "object" ? value : {};
    const safeSlot = Number(src.slotMinutes) > 0 ? Number(src.slotMinutes) : fallbackSlotMinutes;
    const rows = Array.isArray(src.flexible) ? src.flexible : [];
    const flexible = rows
        .map((entry) => ({
            dateFrom: String(entry?.dateFrom || ""),
            week: {
                ...buildEmptyWeekShape(),
                ...(entry?.week && typeof entry.week === "object" ? entry.week : {}),
            },
        }))
        .filter((entry) => entry.dateFrom)
        .sort((a, b) => String(a.dateFrom).localeCompare(String(b.dateFrom)));

    return { slotMinutes: safeSlot, flexible };
}

function formatFlexibleWeekLabel(dateFrom) {
    if (!dateFrom) return "";
    const start = new Date(dateFrom);
    if (Number.isNaN(start.getTime())) return String(dateFrom);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const startText = new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "short",
    }).format(start);
    const endText = new Intl.DateTimeFormat("ru-RU", {
        day: "2-digit",
        month: "short",
    }).format(end);
    return `${startText} — ${endText}`;
}

function parseHmToMinutes(value) {
    const [hRaw, mRaw] = String(value || "00:00").split(":");
    const h = Number.parseInt(hRaw, 10);
    const m = Number.parseInt(mRaw, 10);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
    return Math.max(0, h) * 60 + Math.max(0, m);
}

function minutesToHm(minutes) {
    const total = Math.max(0, Number(minutes) || 0);
    const h = String(Math.floor(total / 60)).padStart(2, "0");
    const m = String(total % 60).padStart(2, "0");
    return `${h}:${m}`;
}

function formatYmdAsRuDots(value) {
    const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return "";
    return `${match[3]}.${match[2]}.${match[1]}`;
}

function validatedYmd(year, month, day) {
    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
    if (y < 1970 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return null;
    const candidate = `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const check = new Date(`${candidate}T00:00:00`);
    if (Number.isNaN(check.getTime())) return null;
    if (
        check.getFullYear() !== y ||
        check.getMonth() + 1 !== m ||
        check.getDate() !== d
    ) {
        return null;
    }
    return candidate;
}

function parseDateInputToYmd(value) {
    const text = String(value || "").trim();
    if (!text) return null;

    let match = text.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (match) return validatedYmd(match[3], match[2], match[1]);

    match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) return validatedYmd(match[1], match[2], match[3]);

    return null;
}

function parseTimeInputToHm(value) {
    const text = String(value || "")
        .trim()
        .replace(/\s+/g, "");
    if (!text) return null;

    let match = text.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
        const h = Number(match[1]);
        const m = Number(match[2]);
        if (!Number.isFinite(h) || !Number.isFinite(m) || h < 0 || h > 23 || m < 0 || m > 59) {
            return null;
        }
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }

    match = text.match(/^(\d{1,2})(?::(\d{2}))?([AP]M)$/i);
    if (match) {
        let h = Number(match[1]);
        const m = Number(match[2] ?? "00");
        const marker = String(match[3] || "").toUpperCase();
        if (!Number.isFinite(h) || !Number.isFinite(m) || h < 1 || h > 12 || m < 0 || m > 59) {
            return null;
        }
        if (marker === "AM") {
            h = h === 12 ? 0 : h;
        } else if (marker === "PM") {
            h = h === 12 ? 12 : h + 12;
        }
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    }

    return null;
}

function addMinutesToHm(value, duration) {
    return minutesToHm(parseHmToMinutes(value) + (Number(duration) || 0));
}

function minutesDiffHm(start, end, fallback = 50) {
    const diff = parseHmToMinutes(end) - parseHmToMinutes(start);
    return diff > 0 ? diff : fallback;
}

function cloneScheduleWeekForFlexible(scheduleValue) {
    const sourceWeek = scheduleValue?.week && typeof scheduleValue.week === "object" ? scheduleValue.week : {};
    const week = buildEmptyWeekShape();
    for (let k = 1; k <= 7; k += 1) {
        week[k] = normalizeIntervalsForDay(Array.isArray(sourceWeek[k]) ? sourceWeek[k] : []);
    }
    return week;
}

function buildEmptyWeekShape() {
    return { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
}

function dayKeyByYmd(ymd) {
    const date = new Date(`${ymd}T00:00:00`);
    const js = date.getDay();
    return js === 0 ? 7 : js;
}

function mondayByYmd(ymd) {
    const date = new Date(`${ymd}T00:00:00`);
    const js = date.getDay();
    const diff = js === 0 ? -6 : 1 - js;
    date.setDate(date.getDate() + diff);
    return dateToYmd(date);
}

function normalizeIntervalsForDay(intervals) {
    const sorted = [...(Array.isArray(intervals) ? intervals : [])]
        .map((item) => ({
            start: String(item?.start || "").slice(0, 5),
            end: String(item?.end || "").slice(0, 5),
        }))
        .filter((item) => item.start && item.end && parseHmToMinutes(item.start) < parseHmToMinutes(item.end))
        .sort((a, b) => parseHmToMinutes(a.start) - parseHmToMinutes(b.start));

    const merged = [];
    sorted.forEach((item) => {
        const prev = merged[merged.length - 1];
        if (!prev) {
            merged.push(item);
            return;
        }
        if (parseHmToMinutes(item.start) <= parseHmToMinutes(prev.end)) {
            if (parseHmToMinutes(item.end) > parseHmToMinutes(prev.end)) {
                prev.end = item.end;
            }
            return;
        }
        merged.push(item);
    });
    return merged;
}

function roundTimeToStep(value, step = 10) {
    const safeStep = Math.max(5, Number(step) || 10);
    const minutes = parseHmToMinutes(value);
    const rounded = Math.ceil(minutes / safeStep) * safeStep;
    return minutesToHm(Math.min(23 * 60 + 55, rounded));
}

function toQuickTime(dateLike, fallback = "10:00") {
    if (!dateLike) return fallback;
    const date = new Date(dateLike);
    if (Number.isNaN(date.getTime())) return fallback;
    return roundTimeToStep(`${pad2(date.getHours())}:${pad2(date.getMinutes())}`, 10);
}

function prettyDateRu(ymd) {
    if (!ymd) return "—";
    const date = new Date(`${ymd}T00:00:00`);
    if (Number.isNaN(date.getTime())) return ymd;
    return new Intl.DateTimeFormat("ru-RU", {
        weekday: "long",
        day: "2-digit",
        month: "long",
    }).format(date);
}

export default function BookingCalendar(props) {
    const {
        psychologist,
        mode = "CLIENT", // CLIENT | PSYCHO
        schedule: scheduleOverride,
        flexibleSchedule: flexibleScheduleProp,
        allowDayOff = false,
        onBooked,
        reloadToken,
    } = props;
    const { me, role, isAuthenticated } = useAuth();
    const toast = useToast();
    const navigate = useNavigate();
    const { connected: wsConnected, subscribe: wsSubscribe } = useWs();

    const effectiveRole =
        mode === "PSYCHO" ? (role === "ADMIN" ? "ADMIN" : "PSYCHOLOGIST") : role;
    const canOpenMyAppointments =
        effectiveRole === "CLIENT" ||
        effectiveRole === "PSYCHOLOGIST" ||
        effectiveRole === "ADMIN";

    const psyId = psychologist?.id;

    const calendarRef = useRef(null);
    const [viewType, setViewType] = useState("timeGridWeek");
    const [range, setRange] = useState(null); // {from,to} YYYY-MM-DD
    const [calendarLabel, setCalendarLabel] = useState("");
    const [miniMonthDate, setMiniMonthDate] = useState(() => startOfDay(new Date()));
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const [loading, setLoading] = useState(false);
    const [schedule, setSchedule] = useState(null);
    const [flexibleSchedule, setFlexibleSchedule] = useState(null);
    const [dayOffs, setDayOffs] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [breaks, setBreaks] = useState([]);

    const [bookingModal, setBookingModal] = useState(null); // booking | null
    const [dayOffModal, setDayOffModal] = useState(null);
    const [quickSlotModal, setQuickSlotModal] = useState(null);
    const [quickSlotDateInput, setQuickSlotDateInput] = useState("");
    const [quickSlotTimeInput, setQuickSlotTimeInput] = useState("");
    const [authRequiredOpen, setAuthRequiredOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savingQuickSlot, setSavingQuickSlot] = useState(false);
    const [savingScheduleConfig, setSavingScheduleConfig] = useState(false);
    const [scheduleEditorOpen, setScheduleEditorOpen] = useState(false);
    const [scheduleMode, setScheduleMode] = useState("FIXED");
    const [scheduleDraft, setScheduleDraft] = useState(null);
    const [flexibleDraft, setFlexibleDraft] = useState(null);
    const bookingInFlightRef = useRef(false);
    const [refunding, setRefunding] = useState(false);

    // ✅ таймер (видимый) для pending брони
    const [holdLeftMs, setHoldLeftMs] = useState(0);
    const paymentStartedRef = useRef(false);
    const autoCancelOnceRef = useRef(false);

    // ✅ платежная UI-информация
    const [paymentUI, setPaymentUI] = useState({
        loading: false,
        checkedOnce: false,
        message: "",
        kind: null, // "info" | "success" | "error"
    });
    const [paymentCode, setPaymentCode] = useState("");
    const [paymentCodeType, setPaymentCodeType] = useState("PROMO"); // PROMO | CERTIFICATE
    const paymentStatusProbeAtRef = useRef(new Map());

    // ✅ возврат с оплаты (bookingId из query)
    const [pendingOpenBookingId, setPendingOpenBookingId] = useState(null);
    const paymentReturnShownRef = useRef(false);

    // ✅ тик “текущее время”, чтобы слоты сами становились недоступными по мере времени
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30_000);
        return () => clearInterval(id);
    }, []);

    // Запись доступна любому авторизованному пользователю.
    const canBook = Boolean(isAuthenticated && me?.id);
    const canManage =
        mode === "PSYCHO" &&
        (effectiveRole === "PSYCHOLOGIST" || effectiveRole === "ADMIN");
    const canManageDayOff = allowDayOff && canManage;
    const bookingMaintenanceBlocked =
        isBookingTemporarilyDisabled() &&
        effectiveRole !== "ADMIN" &&
        !canManage;
    const hasScheduleOverride = scheduleOverride !== undefined;
    const hasFlexibleScheduleOverride = flexibleScheduleProp !== undefined;
    const effectiveSchedule = scheduleOverride || schedule;
    const effectiveFlexibleSchedule = flexibleScheduleProp || flexibleSchedule;
    const canInlineScheduleManage =
        canManage && !hasScheduleOverride && !hasFlexibleScheduleOverride;
    const sidebarSchedule = scheduleDraft || effectiveSchedule;
    const sidebarFlexible = flexibleDraft || effectiveFlexibleSchedule;

    const markedDates = useMemo(() => {
        const out = new Set();

        for (const b of bookings || []) {
            const start = b?.startDateTime || b?.start;
            if (start) out.add(dateToYmd(start));
        }
        for (const off of dayOffs || []) {
            if (off?.date) out.add(dateToYmd(off.date));
        }

        return out;
    }, [bookings, dayOffs]);

    const miniGridDays = useMemo(
        () => buildMiniMonthGrid(miniMonthDate),
        [miniMonthDate]
    );
    const miniMonthLabel = useMemo(
        () => monthLabelRu(miniMonthDate),
        [miniMonthDate]
    );
    const selectedMiniYmd = range?.from || dateToYmd(new Date());

    const weekScheduleRows = useMemo(() => {
        const week = sidebarSchedule?.week || {};
        return [1, 2, 3, 4, 5, 6, 7].map((dayKey) => {
            const intervals = Array.isArray(week?.[dayKey]) ? week[dayKey] : [];
            return {
                dayKey,
                title: WEEKDAY_NAMES[dayKey],
                subtitle: compactIntervalText(intervals),
                enabled: intervals.length > 0,
            };
        });
    }, [sidebarSchedule]);

    const hasFlexibleSchedule =
        Array.isArray(effectiveFlexibleSchedule?.flexible) &&
        effectiveFlexibleSchedule.flexible.length > 0;

    const flexibleWeeks = useMemo(() => {
        if (!Array.isArray(sidebarFlexible?.flexible)) return [];
        return [...sidebarFlexible.flexible].sort((a, b) =>
            String(a?.dateFrom || "").localeCompare(String(b?.dateFrom || ""))
        );
    }, [sidebarFlexible]);

    const quickDurationBase = useMemo(() => {
        return Math.max(
            15,
            Number(sidebarSchedule?.slotMinutes) ||
                Number(effectiveSchedule?.slotMinutes) ||
                50
        );
    }, [effectiveSchedule?.slotMinutes, sidebarSchedule?.slotMinutes]);

    const quickDurationOptions = useMemo(() => {
        return Array.from(new Set([quickDurationBase, ...QUICK_SLOT_DURATIONS])).sort(
            (a, b) => a - b
        );
    }, [quickDurationBase]);

    const quickFlexibleRows = useMemo(() => {
        if (!range?.from) return [];
        const monday = mondayByYmd(range.from);
        const source = ensureFlexibleShape(
            sidebarFlexible || effectiveFlexibleSchedule,
            quickDurationBase
        );
        const weekEntry =
            source.flexible.find((entry) => String(entry?.dateFrom) === monday) || null;
        const start = new Date(`${monday}T00:00:00`);

        return Array.from({ length: 7 }).map((_, index) => {
            const date = addDays(start, index);
            const ymd = dateToYmd(date);
            const dayKey = dayKeyByYmd(ymd);
            const intervals = Array.isArray(weekEntry?.week?.[dayKey])
                ? weekEntry.week[dayKey]
                : [];

            return {
                dayKey,
                ymd,
                label: `${WEEKDAY_SHORT_NAMES[dayKey]} ${String(date.getDate()).padStart(2, "0")}`,
                slots: intervals.map((it) => String(it?.start || "").slice(0, 5)).filter(Boolean),
            };
        });
    }, [effectiveFlexibleSchedule, quickDurationBase, range?.from, sidebarFlexible]);

    useEffect(() => {
        if (!quickSlotModal) {
            setQuickSlotDateInput("");
            setQuickSlotTimeInput("");
            return;
        }
        setQuickSlotDateInput(formatYmdAsRuDots(quickSlotModal.date));
        setQuickSlotTimeInput(String(quickSlotModal.time || ""));
    }, [quickSlotModal]);

    const getCalendarApi = useCallback(() => {
        return calendarRef.current?.getApi?.() || null;
    }, []);

    const handleCalendarPrev = useCallback(() => {
        getCalendarApi()?.prev();
    }, [getCalendarApi]);

    const handleCalendarNext = useCallback(() => {
        getCalendarApi()?.next();
    }, [getCalendarApi]);

    const handleCalendarToday = useCallback(() => {
        getCalendarApi()?.today();
    }, [getCalendarApi]);

    const handleCalendarView = useCallback(
        (nextView) => {
            const api = getCalendarApi();
            if (!api) return;
            api.changeView(nextView);
        },
        [getCalendarApi]
    );

    const handleMiniMonthShift = useCallback((delta) => {
        setMiniMonthDate((prev) =>
            startOfDay(new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
        );
    }, []);

    const handleMiniDayClick = useCallback(
        (dayDate) => {
            const api = getCalendarApi();
            setMiniMonthDate(startOfDay(dayDate));
            setSidebarOpen(false);
            if (!api) return;
            api.gotoDate(dayDate);
        },
        [getCalendarApi]
    );

    const openQuickSlotModal = useCallback(
        ({ date, time, type } = {}) => {
            if (!canManage) return;
            const api = getCalendarApi();
            const anchorDate = date
                ? new Date(`${date}T00:00:00`)
                : api?.getDate?.() || new Date();
            const nextDate = date || dateToYmd(anchorDate);
            const nextTime = time || (date ? "10:00" : toQuickTime(new Date(), "10:00"));
            const duration = quickDurationBase;

            setQuickSlotModal({
                date: nextDate,
                time: nextTime,
                duration,
                type: type || QUICK_SLOT_TYPES.FREE,
                note: "",
                sourceKind: null,
            });
        },
        [canManage, getCalendarApi, quickDurationBase]
    );

    const handlePrimaryAction = useCallback(() => {
        if (mode === "CLIENT") {
            if (bookingMaintenanceBlocked) {
                triggerMaintenanceBanner(toast);
                return;
            }
            if (!canBook) {
                if (!isAuthenticated) setAuthRequiredOpen(true);
                else toast.info("Чтобы записаться, войдите в аккаунт.");
                return;
            }
            const api = getCalendarApi();
            if (api && viewType !== "timeGridDay") {
                api.changeView("timeGridDay", api.getDate());
            }
            toast.info("Выберите свободный слот в сетке календаря.");
            return;
        }

        if (canManage) {
            openQuickSlotModal();
            return;
        }

        toast.info("Выберите запись в календаре для просмотра деталей.");
    }, [
        bookingMaintenanceBlocked,
        canBook,
        canManage,
        getCalendarApi,
        isAuthenticated,
        mode,
        openQuickSlotModal,
        toast,
        viewType,
    ]);

    const toggleDayInScheduleDraft = useCallback(
        (dayKey) => {
            if (!canInlineScheduleManage) return;
            setScheduleDraft((prev) => {
                const base = ensureScheduleShape(prev || effectiveSchedule);
                const intervals = Array.isArray(base.week?.[dayKey]) ? base.week[dayKey] : [];
                const nextIntervals = intervals.length ? [] : [{ start: "10:00", end: "18:00" }];
                return {
                    ...base,
                    week: {
                        ...base.week,
                        [dayKey]: nextIntervals,
                    },
                };
            });
        },
        [canInlineScheduleManage, effectiveSchedule]
    );

    const updateSlotMinutesInDraft = useCallback(
        (rawValue) => {
            if (!canInlineScheduleManage) return;
            const nextSlot = Math.max(15, Number(rawValue) || 50);
            setScheduleDraft((prev) => {
                const base = ensureScheduleShape(prev || effectiveSchedule);
                return {
                    ...base,
                    slotMinutes: nextSlot,
                };
            });
        },
        [canInlineScheduleManage, effectiveSchedule]
    );

    const saveFixedSchedule = useCallback(
        async (value) => {
            if (!psyId || !canInlineScheduleManage) return;
            const draftValue = ensureScheduleShape(value || scheduleDraft || effectiveSchedule);
            setSavingScheduleConfig(true);
            try {
                const saved = await sessionsApi.upsertSchedule(psyId, draftValue);
                const normalized = ensureScheduleShape(saved || draftValue);
                setSchedule(normalized);
                setScheduleDraft(cloneDeep(normalized));
                toast.success("Фиксированный график сохранён");
            } catch (err) {
                toast.error(err?.message || "Не удалось сохранить фиксированный график");
            } finally {
                setSavingScheduleConfig(false);
            }
        },
        [canInlineScheduleManage, effectiveSchedule, psyId, scheduleDraft, toast]
    );

    const saveFlexibleSchedule = useCallback(
        async (value) => {
            if (!psyId || !canInlineScheduleManage) return;
            const slotMinutes =
                Number(scheduleDraft?.slotMinutes) ||
                Number(effectiveSchedule?.slotMinutes) ||
                50;
            const draftValue = cloneDeep(
                value ||
                    sidebarFlexible || {
                        slotMinutes,
                        flexible: [],
                    }
            );
            setSavingScheduleConfig(true);
            try {
                const saved = await sessionsApi.saveFlexibleSchedule(psyId, draftValue);
                const normalized =
                    Array.isArray(saved?.flexible) && saved.flexible.length ? saved : null;
                setFlexibleSchedule(normalized);
                setFlexibleDraft(cloneDeep(saved || draftValue));
                toast.success("Гибкий график сохранён");
            } catch (err) {
                toast.error(err?.message || "Не удалось сохранить гибкий график");
            } finally {
                setSavingScheduleConfig(false);
            }
        },
        [canInlineScheduleManage, effectiveSchedule?.slotMinutes, psyId, scheduleDraft?.slotMinutes, sidebarFlexible, toast]
    );

    const saveCurrentScheduleMode = useCallback(async () => {
        if (scheduleMode === "FLEXIBLE") {
            await saveFlexibleSchedule();
            return;
        }
        await saveFixedSchedule();
    }, [saveFixedSchedule, saveFlexibleSchedule, scheduleMode]);

    // загрузка данных
    const load = useCallback(async () => {
        if (!psyId || !range) return;
        setLoading(true);
        try {

            const [sc, flexSc, offs, bks, brs] = await Promise.all([
                hasScheduleOverride
                    ? Promise.resolve(scheduleOverride)
                    : sessionsApi.getSchedule(psyId),
                hasFlexibleScheduleOverride
                    ? Promise.resolve(flexibleScheduleProp)
                    : sessionsApi.getFlexibleSchedule(psyId, range),
                sessionsApi.listDayOffs(psyId, range),
                mode === "CLIENT"
                    ? sessionsApi.listCalendar(psyId, range)
                    : sessionsApi.listBookings(psyId, range),
                sessionsApi.listWorkBreaks(psyId, range),
            ]);

            const normalizedSchedule = sc || null;
            const normalizedFlexible =
                Array.isArray(flexSc?.flexible) && flexSc.flexible.length
                    ? flexSc
                    : null;

            setSchedule(normalizedSchedule);
            setFlexibleSchedule(normalizedFlexible);
            setScheduleDraft(cloneDeep(normalizedSchedule));
            setFlexibleDraft(cloneDeep(normalizedFlexible));
            setScheduleMode(normalizedFlexible ? "FLEXIBLE" : "FIXED");

            // нормализуем дату ("YYYY-MM-DD" / ISO) → "YYYY-MM-DD" без сдвига по таймзоне
            setDayOffs(
                Array.isArray(offs)
                    ? offs.map((x) => {
                          const id =
                              x?.id ??
                              x?.dayOffId ??
                              x?.dayoffId ??
                              x?.day_off_id ??
                              x?.day_offId ??
                              x?.dayoff_id ??
                              null;
                          return { ...x, id, date: dateToYmd(x?.date) };
                      })
                    : []
            );
            setBookings(Array.isArray(bks) ? bks.map((b) => normalizeBookingForUi(b)) : []);
            setBreaks(Array.isArray(brs) ? brs : []);
        } catch (err) {
            console.error("Ошибка загрузки календаря", err);
            toast.error("Не удалось загрузить расписание");
        } finally {
            setLoading(false);
        }
    }, [
        psyId,
        range,
        hasScheduleOverride,
        hasFlexibleScheduleOverride,
        scheduleOverride,
        flexibleScheduleProp,
        mode,
        toast,
    ]);

    useEffect(() => {
        load();
    }, [load, reloadToken]);


    // ✅ если бронь стала PAID (через WS/обновление) — снимаем её из счётчика «заморозок без оплаты»
    useEffect(() => {
        if (!me?.id) return;
        const attempts = readHoldAttempts(me.id);
        if (!attempts.length) return;

        const paidIds = new Set(
            (bookings || [])
                .filter((b) => String(b?.status || "").toUpperCase() === "PAID")
                .map((b) => String(b.id))
        );

        if (!paidIds.size) return;

        const next = attempts.filter((a) => !paidIds.has(String(a.bookingId)));
        if (next.length !== attempts.length) writeHoldAttempts(me.id, next);
    }, [bookings, me?.id]);

    // WebSocket подписка
    useEffect(() => {
        if (!wsConnected || !psyId) return;
        const topic = `/topic/booking/psychologist/${psyId}`;
        return wsSubscribe(topic, () => {
            if (range) load();
        });
    }, [wsConnected, wsSubscribe, psyId, range, load]);

    useEffect(() => {
        if (hasScheduleOverride) {
            const normalized = scheduleOverride || null;
            setSchedule(normalized);
            setScheduleDraft(cloneDeep(normalized));
        }
    }, [hasScheduleOverride, scheduleOverride]);

    useEffect(() => {
        if (hasFlexibleScheduleOverride) {
            const normalized =
                Array.isArray(flexibleScheduleProp?.flexible) && flexibleScheduleProp.flexible.length
                    ? flexibleScheduleProp
                    : null;
            setFlexibleSchedule(normalized);
            setFlexibleDraft(cloneDeep(normalized));
            setScheduleMode(normalized ? "FLEXIBLE" : "FIXED");
        }
    }, [hasFlexibleScheduleOverride, flexibleScheduleProp]);

    // ✅ читаем query возврата с оплаты один раз
    useEffect(() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            const payment = sp.get("payment");
            const bookingId = sp.get("bookingId");
            if (payment === "return" && bookingId) {
                setPendingOpenBookingId(String(bookingId));
            }
        } catch {}
    }, []);

    // ✅ если мы вернулись с оплаты — покажем toast и обновим календарь
    useEffect(() => {
        if (!pendingOpenBookingId) return;
        paymentReturnShownRef.current = true;

        if (psyId && range) load();
    }, [pendingOpenBookingId, psyId, range, load, toast]);

    // ✅ открыть модалку ПОСЛЕ загрузки брони (после возврата)
    useEffect(() => {
        if (!pendingOpenBookingId) return;
        if (loading) return;

        const found = (bookings || []).find(
            (b) => String(b?.id) === String(pendingOpenBookingId)
        );
        if (!found) return;

        const rememberedHold = getRememberedHoldExpiresAt(found.id);

        setBookingModal({
            ...found,
            holdExpiresAt: rememberedHold || null,
            fromReturn: true,
        });

        setPaymentUI({
            loading: false,
            checkedOnce: false,
            message: "Оплата проверяется…",
            kind: "info",
        });

        clearPaymentQueryParams();
        setPendingOpenBookingId(null);
    }, [pendingOpenBookingId, loading, bookings]);

    // ✅ брони, которые реально блокируют доступность слота
    const blockingBookings = useMemo(() => {
        return (bookings || []).filter((b) => isBlockingBookingStatus(b?.status));
    }, [bookings]);

    // Для календаря психолога: подтягиваем реальный статус оплаты по pending-записям,
    // потому что базовый список бронирований может приходить с задержкой.
    useEffect(() => {
        const isPsychCalendar = mode === "PSYCHO" || effectiveRole === "PSYCHOLOGIST" || effectiveRole === "ADMIN";
        if (!isPsychCalendar || loading) return;

        const pendingBookings = (bookings || []).filter(
            (b) => b?.id != null && isAwaitingPaymentStatus(b?.status) && !isBookingConsideredPaid(b)
        );
        if (!pendingBookings.length) return;

        const now = Date.now();
        const due = pendingBookings
            .filter((b) => {
                const id = String(b.id);
                const last = Number(paymentStatusProbeAtRef.current.get(id) || 0);
                return now - last >= PAYMENT_STATUS_REFRESH_INTERVAL_MS;
            })
            .slice(0, PAYMENT_STATUS_REFRESH_BATCH);
        if (!due.length) return;

        due.forEach((b) => paymentStatusProbeAtRef.current.set(String(b.id), now));

        let cancelled = false;

        (async () => {
            const probes = await Promise.all(
                due.map(async (b) => {
                    try {
                        const res = await sessionsApi.getPaymentStatus(b.id);
                        const norm = normalizePaymentStatusResponse(res);
                        const paymentStatus = normalizeToken(norm?.paymentStatus);
                        const bookingStatus = normalizeToken(norm?.bookingStatus);

                        let nextStatus = bookingStatus || normalizeToken(b?.status);
                        if (isPaidBookingStatus(paymentStatus) || isPaidBookingStatus(bookingStatus)) {
                            nextStatus = "PAID";
                        } else if (isFailedPaymentStatus(paymentStatus)) {
                            nextStatus = bookingStatus || "CANCELLED";
                        } else if (!nextStatus) {
                            nextStatus = "PENDING_PAYMENT";
                        }

                        return {
                            id: String(b.id),
                            status: nextStatus,
                            paymentStatus: paymentStatus || null,
                        };
                    } catch {
                        return null;
                    }
                })
            );

            if (cancelled) return;

            const updates = new Map(
                probes
                    .filter(Boolean)
                    .map((item) => [String(item.id), item])
            );
            if (!updates.size) return;

            setBookings((prev) =>
                (prev || []).map((b) => {
                    const upd = updates.get(String(b?.id));
                    if (!upd) return b;

                    const nextRaw = normalizeBookingForUi({
                        ...b,
                        status: upd.status || b?.status,
                        paymentStatus: upd.paymentStatus || b?.paymentStatus || null,
                    });

                    if (
                        normalizeToken(nextRaw?.status) === normalizeToken(b?.status) &&
                        normalizeToken(nextRaw?.paymentStatus) === normalizeToken(b?.paymentStatus)
                    ) {
                        return b;
                    }
                    return nextRaw;
                })
            );

            setBookingModal((prev) => {
                if (!prev?.id) return prev;
                const upd = updates.get(String(prev.id));
                if (!upd) return prev;
                return normalizeBookingForUi({
                    ...prev,
                    status: upd.status || prev?.status,
                    paymentStatus: upd.paymentStatus || prev?.paymentStatus || null,
                });
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [bookings, effectiveRole, loading, mode]);

    // ✅ определение "моя ли бронь" (строго: только брони текущего пользователя)
    // ВАЖНО: роль ADMIN не означает "моя бронь" — иначе появятся кнопки отмены/возврата на чужих записях.
    const isMineBooking = useCallback(
        (b) => {
            if (!b) return false;

            const cid = getClientIdFromBooking(b);
            if (cid && me?.id && String(cid) === String(me.id)) return true;

            // backend-флаг mine считаем приоритетным для любого пользователя
            if (b?.mine != null) return Boolean(b.mine);

            // fallback: если мы сами создавали бронь на этом устройстве
            if (b?.id && me?.id && isRememberedOwnBooking(b.id, me.id)) return true;

            return false;
        },
        [me?.id]
    );

    const upcomingBookingItems = useMemo(() => {
        const nowMs = Date.now();

        return (bookings || [])
            .map((raw) => {
                const start = raw?.startDateTime || raw?.start || null;
                const startMs = start ? new Date(start).getTime() : NaN;
                const status = String(raw?.status || "").toUpperCase();
                const mine = isMineBooking(raw);
                const awaiting = isAwaitingPaymentStatus(status);

                if (!Number.isFinite(startMs) || startMs < nowMs) return null;
                if (!isVisibleBookingStatusInCalendar(status)) return null;
                if (effectiveRole === "CLIENT" && !mine) return null;

                let badge = "Новая";
                let badgeClass = "is-new";
                if (awaiting) {
                    badge = "Ожидание";
                    badgeClass = "is-wait";
                } else if (startMs - nowMs <= 24 * 60 * 60 * 1000) {
                    badge = "Скоро";
                    badgeClass = "is-soon";
                }

                const title =
                    raw?.clientName ||
                    raw?.title ||
                    (mine ? "Ваша запись" : "Клиент");

                return {
                    id: raw?.id ?? `${startMs}-${title}`,
                    startMs,
                    title,
                    time: new Date(startMs).toLocaleString("ru-RU", {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    badge,
                    badgeClass,
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.startMs - b.startMs)
            .slice(0, 6);
    }, [bookings, effectiveRole, isMineBooking]);

    const events = useMemo(() => {
        if (!psyId) return [];
        const sc = scheduleOverride || schedule;
        const effectiveFlexibleSchedule = flexibleScheduleProp || flexibleSchedule;
        const now = new Date(nowTick);

        // ✅ booking events — ВСЕ интерактивны
        const bookingEvents = (bookings || [])
            .filter((b) => isVisibleBookingStatusInCalendar(b?.status))
            .map((b) => {
                const base = normalizeBookingToEvent(b, { role: effectiveRole }) || {};
                const status = String(b?.status || "");
                const awaiting = isAwaitingPaymentStatus(status);
                const mine = isMineBooking(b);
                // ✅ подпись для "моей" записи
                // - для pending: «Ожидает оплаты»
                // - для остальных: «Ваша запись» (понятно, что это именно вы)
                const title =
                    awaiting && mine
                        ? "Ожидает оплаты"
                        : mine
                            ? "Ваша запись"
                            : base.title || "Занято";

                const classNames = Array.isArray(base.classNames)
                    ? [...base.classNames]
                    : base.className
                        ? [base.className]
                        : [];

                if (awaiting && mine) classNames.push("b-event--booking-pending-mine");

                return {
                    ...base,
                    title,
                    classNames,
                    display: "auto",
                    interactive: true,
                    extendedProps: {
                        ...(base.extendedProps || {}),
                        kind: "BOOKING",
                        raw: b,
                        mine,
                    },
                };
            })
            .filter(Boolean);

        const dayOffEvents = (dayOffs || []).map(normalizeDayOffToEvent);

        const breakEvents = range
            ? buildBreakEvents({
                rangeStart: new Date(`${range.from}T00:00:00`),
                rangeEnd: new Date(
                    new Date(`${range.to}T00:00:00`).getTime() + 86400000
                ),
                breaks,
            })
            : [];

        const slotEventsRaw =
            (sc || effectiveFlexibleSchedule) && range && viewType.startsWith("timeGrid")
                ? buildSlotEvents({
                    rangeStart: new Date(`${range.from}T00:00:00`),
                    rangeEnd: new Date(
                        new Date(`${range.to}T00:00:00`).getTime() + 86400000
                    ),
                    schedule: sc,
                    flexibleSchedule: effectiveFlexibleSchedule,
                    dayOffs,
                    bookings: blockingBookings,
                    breaks,
                })
                : [];

        const slotEvents = (slotEventsRaw || []).map((ev) => {
            const start = ev?.start ? new Date(ev.start) : null;
            const bookable = start ? slotBookable(start, now) : false;

            if (bookable) {
                return {
                    ...ev,
                    extendedProps: { ...(ev.extendedProps || {}), bookable: true },
                };
            }

            const classNames = Array.isArray(ev.classNames)
                ? [...ev.classNames, "b-event--slot-locked"]
                : ev.className
                    ? [ev.className, "b-event--slot-locked"]
                    : ["b-event--slot-locked"];

            return {
                ...ev,
                title: "Запись закрыта",
                classNames,
                extendedProps: { ...(ev.extendedProps || {}), bookable: false },
            };
        });

        return [...dayOffEvents, ...breakEvents, ...slotEvents, ...bookingEvents];
    }, [
        psyId,
        schedule,
        scheduleOverride,
        flexibleSchedule,
        flexibleScheduleProp,
        dayOffs,
        bookings,
        blockingBookings,
        breaks,
        range,
        viewType,
        effectiveRole,
        nowTick,
        isMineBooking,
    ]);

    const upcomingFreeSlotItems = useMemo(() => {
        const nowMs = Date.now();
        return (events || [])
            .filter((event) => event?.extendedProps?.kind === "SLOT")
            .map((event, index) => {
                const startMs = event?.start ? new Date(event.start).getTime() : NaN;
                if (!Number.isFinite(startMs) || startMs < nowMs) return null;
                return {
                    id: event.id || `slot-upcoming-${index}`,
                    startMs,
                    title: "Свободный слот",
                    time: new Date(startMs).toLocaleString("ru-RU", {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                    }),
                    badge: "Свободно",
                    badgeClass: "is-soon",
                };
            })
            .filter(Boolean)
            .sort((a, b) => a.startMs - b.startMs)
            .slice(0, 6);
    }, [events]);

    const upcomingItems = useMemo(() => {
        if (upcomingBookingItems.length) return upcomingBookingItems;
        return upcomingFreeSlotItems;
    }, [upcomingBookingItems, upcomingFreeSlotItems]);

    const upcomingSectionTitle = useMemo(() => {
        if (upcomingBookingItems.length) {
            return effectiveRole === "CLIENT" ? "Ваши записи" : "Ближайшие записи";
        }
        if (upcomingFreeSlotItems.length) {
            return "Ближайшие свободные слоты";
        }
        return effectiveRole === "CLIENT" ? "Ваши записи" : "Ближайшие записи";
    }, [effectiveRole, upcomingBookingItems.length, upcomingFreeSlotItems.length]);

    const onDatesSet = (arg) => {
        setViewType(arg.view.type);
        setCalendarLabel(arg.view.title || "");
        const from = dateToYmd(arg.start);
        const endInclusive = new Date(arg.end);
        endInclusive.setDate(endInclusive.getDate() - 1);
        const to = dateToYmd(endInclusive);
        setRange({ from, to });
        setMiniMonthDate(startOfDay(arg.start));
    };

    const cancelBookingById = useCallback(
        async (bookingId, { silent = false, toastMsg = "Бронь отменена" } = {}) => {
            if (!bookingId) return;
            setSaving(true);
            try {
                await sessionsApi.cancelBooking(bookingId);
                setBookings((prev) => prev.filter((b) => b.id !== bookingId));
                clearRememberedHoldExpiresAt(bookingId);
                clearRememberedPaymentStarted(bookingId);
                if (me?.id) removeHoldAttempt(me.id, bookingId);
                if (!silent) toast.info(toastMsg);
            } catch (err) {
                console.error(err);
                toast.error("Не удалось отменить бронь");
            } finally {
                setSaving(false);
            }
        },
        [me?.id, toast]
    );

    // ✅ сброс флагов при открытии новой брони
    useEffect(() => {
        paymentStartedRef.current = false;
        autoCancelOnceRef.current = false;
        setPaymentCode("");
        setPaymentCodeType("PROMO");

        // сбрасываем UI оплаты при смене bookingModal
        setPaymentUI((prev) => ({
            ...prev,
            loading: false,
            checkedOnce: false,
            message: "",
            kind: null,
        }));
    }, [bookingModal?.id]);

    const awaitingPayment = useMemo(() => {
        if (!bookingModal) return false;
        if (isBookingConsideredPaid(bookingModal)) return false;
        if (bookingModal?.justCreated === true) return true;
        return isAwaitingPaymentStatus(bookingModal?.status);
    }, [bookingModal]);

    const isOwnBooking = useMemo(() => {
        if (!bookingModal) return false;
        return isMineBooking(bookingModal);
    }, [bookingModal, isMineBooking]);

    const canPayThisBooking = canBook && awaitingPayment && isOwnBooking;

    const goToMyAppointments = useCallback(
        (bookingId = null) => {
            if (!canOpenMyAppointments) return false;
            const target = bookingId
                ? `${MY_APPOINTMENTS_ROUTE}?bookingId=${encodeURIComponent(String(bookingId))}`
                : MY_APPOINTMENTS_ROUTE;
            navigate(target);
            return true;
        },
        [canOpenMyAppointments, navigate]
    );

    const paidBooking = useMemo(() => {
        if (!bookingModal) return false;
        return isBookingConsideredPaid(bookingModal);
    }, [bookingModal]);

    const bookingStartMs = useMemo(() => {
        const s = bookingModal?.startDateTime ? new Date(bookingModal.startDateTime).getTime() : null;
        return Number.isFinite(s) ? s : null;
    }, [bookingModal?.startDateTime]);

    const isFutureBooking = useMemo(() => {
        if (!bookingStartMs) return false;
        return bookingStartMs > Date.now();
    }, [bookingStartMs]);

    // ✅ возврат обычно возможен, если до старта больше 24 часов
    const canRefundThisBooking = useMemo(() => {
        if (!paidBooking || !isOwnBooking || !isFutureBooking) return false;
        if (!bookingStartMs) return false;
        return bookingStartMs - Date.now() >= 24 * 60 * 60 * 1000;
    }, [paidBooking, isOwnBooking, isFutureBooking, bookingStartMs]);

    const canCancelPaidBooking = useMemo(() => {
        return paidBooking && isOwnBooking && isFutureBooking;
    }, [paidBooking, isOwnBooking, isFutureBooking]);


    // ✅ восстановим holdExpiresAt из localStorage если есть
    useEffect(() => {
        if (!bookingModal?.id) return;
        if (!awaitingPayment) return;
        if (bookingModal.holdExpiresAt) return;

        const remembered = getRememberedHoldExpiresAt(bookingModal.id);
        if (!remembered) return;

        setBookingModal((prev) =>
            prev?.id === bookingModal.id ? { ...prev, holdExpiresAt: remembered } : prev
        );
    }, [bookingModal?.id, bookingModal?.holdExpiresAt, awaitingPayment]);

    // ✅ обратный отсчёт + автоснятие по истечению HOLD (front fallback)
    useEffect(() => {
        if (!bookingModal?.id) {
            setHoldLeftMs(0);
            return;
        }
        if (!awaitingPayment) {
            setHoldLeftMs(0);
            return;
        }
        if (!bookingModal?.holdExpiresAt) {
            setHoldLeftMs(0);
            return;
        }

        let alive = true;

        const tick = () => {
            if (!alive) return;

            const left = bookingModal.holdExpiresAt - Date.now();
            const safe = Math.max(0, left);
            setHoldLeftMs(safe);

            if (safe <= 0) {
                if (paymentStartedRef.current) return;
                if (autoCancelOnceRef.current) return;
                autoCancelOnceRef.current = true;

                const id = bookingModal.id;
                setBookingModal(null);
                void cancelBookingById(id, { silent: true });
                toast.info("Время ожидания оплаты истекло — бронь отменена.", {
                    title: "Бронь снята",
                });
            }
        };

        tick();
        const intervalId = setInterval(tick, 1000);

        return () => {
            alive = false;
            clearInterval(intervalId);
        };
    }, [bookingModal?.id, bookingModal?.holdExpiresAt, awaitingPayment, cancelBookingById, toast]);

    // ✅ показывать кнопку "Проверить статус" только если пользователь реально уходил на оплату
    const showCheckStatusBtn = useMemo(() => {
        if (!bookingModal?.id) return false;
        if (!awaitingPayment) return false;
        if (!isOwnBooking) return false;

        // ✅ если вернулся с оплаты — точно показываем
        if (bookingModal?.fromReturn) return true;

        // ✅ если ранее нажимал оплатить (уходил на платеж)
        return isRememberedPaymentStarted(bookingModal.id);
    }, [bookingModal?.id, bookingModal?.fromReturn, awaitingPayment, isOwnBooking]);

    const onEventClick = (info) => {
        const kind =
            info.event.extendedProps?.kind ||
            info.event.extendedProps?.type ||
            (info.event.extendedProps?.raw ? "BOOKING" : null);

        if (kind === "SLOT") {
            const start = info.event.start;
            const end = info.event.end;

            if (canManage) {
                const safeStart = start || new Date();
                const duration = Math.max(
                    15,
                    Math.round(
                        (end ? end.getTime() : safeStart.getTime() + quickDurationBase * 60000) -
                            safeStart.getTime()
                    ) / 60000
                );
                setQuickSlotModal({
                    date: dateToYmd(safeStart),
                    time: toQuickTime(safeStart, "10:00"),
                    duration: Number.isFinite(duration) ? duration : quickDurationBase,
                    type: QUICK_SLOT_TYPES.FREE,
                    note: "",
                    sourceKind: "SLOT",
                    sourceStart: start ? start.toISOString() : null,
                    sourceEnd: end ? end.toISOString() : null,
                });
                return;
            }

            if (bookingMaintenanceBlocked) {
                triggerMaintenanceBanner(toast);
                return;
            }

            if (!canBook) {
                if (mode === "CLIENT" && !isAuthenticated) setAuthRequiredOpen(true);
                else toast.info("Чтобы записаться, войдите в аккаунт.");
                return;
            }

            const bookable = info.event.extendedProps?.bookable;
            if (!start || !end || bookable === false) {
                toast.info(
                    `Запись закрыта: нужно минимум ${MIN_BOOK_AHEAD_MIN} минут до начала.`,
                    { title: "Недоступно" }
                );
                return;
            }

            handleCreateBooking(start, end);
            return;
        }

        if (kind === "DAYOFF") {
            if (!canManageDayOff) {
                toast.info("Выходные можно менять только в вашем графике.", {
                    title: "Недоступно",
                });
                return;
            }
            const raw = info.event.extendedProps?.raw;
            const resolvedId =
                raw?.id ??
                raw?.dayOffId ??
                raw?.dayoffId ??
                raw?.day_off_id ??
                raw?.day_offId ??
                raw?.dayoff_id ??
                null;
            setDayOffModal({
                date: raw?.date,
                reason: raw?.reason || "",
                existingId: resolvedId,
                exists: true,
            });
            return;
        }


        if (kind === "BREAK" || kind === "BUSY") {
            if (!canManage) return;
            const raw = info.event.extendedProps?.raw || {};
            const start = info.event.start || new Date();
            const end = info.event.end || addMinutes(start, quickDurationBase);
            const time = toQuickTime(start, "10:00");
            const endTime = toQuickTime(end, addMinutesToHm(time, quickDurationBase));
            const duration = Math.max(15, Math.round((end.getTime() - start.getTime()) / 60000));
            setQuickSlotModal({
                date: dateToYmd(start),
                time,
                duration: Number.isFinite(duration) ? duration : minutesDiffHm(time, endTime, quickDurationBase),
                type: kind === "BUSY" ? QUICK_SLOT_TYPES.BUSY : QUICK_SLOT_TYPES.BREAK,
                note: raw?.note || raw?.reason || raw?.comment || "",
                existingBreakId:
                    raw?.id ??
                    raw?.breakId ??
                    raw?.workBreakId ??
                    raw?.work_break_id ??
                    null,
                sourceKind: kind,
            });
            return;
        }

        if (kind === "BOOKING") {
            const raw = info.event.extendedProps?.raw || null;
            if (!raw) return;

            const rememberedHold = raw?.id
                ? getRememberedHoldExpiresAt(raw.id)
                : null;

            setBookingModal({
                ...raw,
                holdExpiresAt: rememberedHold || raw?.holdExpiresAt || null,
                justCreated: false,
                fromReturn: false,
            });
        }
    };

    const handleCreateBooking = async (start, end) => {
        if (bookingMaintenanceBlocked) {
            triggerMaintenanceBanner(toast);
            return;
        }

        if (!psyId) return;
        if (!me?.id && effectiveRole !== "ADMIN") {
            if (mode === "CLIENT" && !isAuthenticated) setAuthRequiredOpen(true);
            return;
        }

        const now = new Date();
        if (!slotBookable(start, now)) {
            toast.info(
                `Запись закрыта: нужно минимум ${MIN_BOOK_AHEAD_MIN} минут до начала.`,
                { title: "Недоступно" }
            );
            return;
        }

        // ✅ лимит: не более 3 «заморозок» без оплаты за последний час
        if (effectiveRole !== "ADMIN" && me?.id) {
            const cnt = countHoldAttemptsLastHour(me.id);
            if (cnt >= HOLD_LIMIT_PER_HOUR) {
                toast.info(
                    "За последний час вы уже забронировали 3 слота без оплаты. Оплатите любой из них, чтобы продолжить, или подождите.",
                    { title: "Лимит бронирований" }
                );
                return;
            }
        }

        // ✅ синхронный лок: не даём запускать несколько броней параллельно (иначе лимит можно обойти быстрыми кликами)
        if (bookingInFlightRef.current) return;
        bookingInFlightRef.current = true;

        setSaving(true);
        try {
            const created = await sessionsApi.createBooking(psyId, {
                startDateTime: start.toISOString(),
                endDateTime: end.toISOString(),
                price: psychologist?.priceAtTime ?? null,
            });

            setBookings((prev) => [...prev, created]);

            // ✅ старт таймера удержания
            const holdExpiresAt = Date.now() + HOLD_PAYMENT_MS;

            if (me?.id && created?.id) {
                rememberOwnBooking(created.id, me.id);
                rememberHoldExpiresAt(created.id, holdExpiresAt);
                clearRememberedPaymentStarted(created.id); // ✅ на старте оплаты НЕ было

                // ✅ переносим «оптимистичную» запись на реальный bookingId (или удаляем, если бронь не в статусе ожидания оплаты)
                if (isAwaitingPaymentStatus(created.status)) {
                    addHoldAttempt(me.id, created.id);
                }
            }

            setBookingModal({ ...created, justCreated: true, holdExpiresAt, fromReturn: false });

            if (typeof onBooked === "function") onBooked(created);

            toast.success("Слот забронирован, ожидает оплаты");
        } catch (err) {
            console.error(err);
            toast.error(
                err?.message || "Не удалось забронировать слот. Возможно, он уже занят."
            );
        } finally {
            setSaving(false);
            bookingInFlightRef.current = false;
        }
    };

    const handleCancelBooking = async () => {
        if (!bookingModal?.id) return;
        if (!isOwnBooking) {
            toast.info("Отменить можно только свою запись");
            return;
        }
        autoCancelOnceRef.current = true;
        const id = bookingModal.id;
        await cancelBookingById(id);
        setBookingModal(null);
    };

    // ✅ отменить уже оплаченную консультацию (если доступно на бэке)
    const handleCancelPaidBooking = async () => {
        if (!bookingModal?.id) return;
        if (!isOwnBooking) {
            toast.info("Отменить можно только свою сессию");
            return;
        }
        autoCancelOnceRef.current = true;
        const id = bookingModal.id;

        try {
            await cancelBookingById(id, { toastMsg: "Сессия отменена" });
            setBookingModal(null);
        } catch (e) {
            // cancelBookingById уже покажет toast
        }
    };

    function extractPaymentId(b) {
        return (
            b?.paymentId ??
            b?.payment_id ??
            b?.payment?.id ??
            b?.lastPaymentId ??
            b?.last_payment_id ??
            null
        );
    }

    // ✅ запрос возврата денег по paymentId (если до сессии > 24ч)
    const handleRefundBooking = async () => {
        if (!bookingModal?.id) return;
        if (!isOwnBooking) {
            toast.info("Вернуть деньги можно только по своей записи");
            return;
        }
        if (!canRefundThisBooking) {
            toast.info("Возврат доступен, если до начала сессии больше 24 часов.");
            return;
        }

        setRefunding(true);
        try {
            let paymentId = extractPaymentId(bookingModal);

            // если в модалке не было paymentId — попробуем получить детали брони
            if (!paymentId) {
                try {
                    const full = await sessionsApi.getBooking(bookingModal.id);
                    paymentId = extractPaymentId(full);
                } catch (e) {
                    // ignore
                }
            }

            if (!paymentId) {
                toast.error("Не удалось определить paymentId для возврата (нужно добавить в DTO брони)");
                return;
            }

            await paymentsApi.refund(paymentId);

            toast.success("Запрос на возврат отправлен ✅");
            setBookingModal(null);

            // обновим календарь, чтобы слот освободился, если бэк так делает
            await load();
        } catch (e) {
            console.error(e);
            toast.error(e?.message || "Не удалось оформить возврат");
        } finally {
            setRefunding(false);
        }
    };


    // ✅ запрос статуса оплаты
    const handleCheckPaymentStatus = async (bookingId) => {
        if (!bookingId) return;
        const isReturnFlowForCurrentBooking =
            Boolean(bookingModal?.fromReturn) &&
            String(bookingModal?.id || "") === String(bookingId);

        setPaymentUI({
            loading: true,
            checkedOnce: true,
            message: "Оплата проверяется…",
            kind: "info",
        });

        try {
            const res = await sessionsApi.getPaymentStatus(bookingId);

            const norm = normalizePaymentStatusResponse(res);

            // обновляем бронь в списке и модалке, если bookingStatus пришёл
            if (norm?.bookingStatus) {
                setBookings((prev) =>
                    (prev || []).map((b) =>
                        String(b?.id) === String(bookingId)
                            ? { ...b, status: norm.bookingStatus }
                            : b
                    )
                );

                setBookingModal((prev) =>
                    prev?.id && String(prev.id) === String(bookingId)
                        ? { ...prev, status: norm.bookingStatus }
                        : prev
                );
            }

            // UI сообщение
            const paymentStatus = norm?.paymentStatus;
            const bookingStatus = norm?.bookingStatus;

            // 1) успех
            if (isPaidBookingStatus(paymentStatus) || isPaidBookingStatus(bookingStatus)) {
                clearRememberedHoldExpiresAt(bookingId);
                clearRememberedPaymentStarted(bookingId);
                if (me?.id) removeHoldAttempt(me.id, bookingId);

                setPaymentUI({
                    loading: false,
                    checkedOnce: true,
                    message: "✅ Оплата подтверждена. Бронь успешно оплачена.",
                    kind: "success",
                });

                toast.success("✅ Оплата подтверждена");
                setBookingModal(null);
                if (!goToMyAppointments(bookingId)) {
                    toast.info("Бронь оплачена. Откройте раздел «Мои записи».");
                }
                return;
            }

            // 2) ещё ожидаем
            if (isAwaitingPaymentStatus(paymentStatus) || isAwaitingPaymentStatus(bookingStatus)) {
                setPaymentUI({
                    loading: false,
                    checkedOnce: true,
                    message:
                        "Оплата проверяется… Если статус не обновился — нажмите «Проверить статус» ещё раз.",
                    kind: "info",
                });
                return;
            }

            // 3) отмена / ошибка
            if (paymentStatus === "CANCELLED" || paymentStatus === "FAILED" || bookingStatus === "CANCELLED") {
                clearRememberedPaymentStarted(bookingId);

                setPaymentUI({
                    loading: false,
                    checkedOnce: true,
                    message:
                        "❌ Оплата не завершена (отменена или не подтверждена). Вы можете попробовать оплатить снова.",
                    kind: "error",
                });
                if (isReturnFlowForCurrentBooking) {
                    setBookingModal(null);
                    toast.info("Оплата не завершена. Вы вернулись в календарь.");
                } else {
                    toast.error("Оплата не подтверждена");
                }
                return;
            }

            // 4) неизвестно — но покажем аккуратно
            setPaymentUI({
                loading: false,
                checkedOnce: true,
                message: `Статус оплаты: ${paymentStatus || "—"}, бронь: ${bookingStatus || "—"}`,
                kind: "info",
            });
        } catch (e) {
            console.error(e);
            setPaymentUI({
                loading: false,
                checkedOnce: true,
                message: "Не удалось проверить статус оплаты. Попробуйте ещё раз.",
                kind: "error",
            });
            toast.error("Не удалось проверить статус оплаты");
        }
    };

    // ✅ если вернулись с оплаты — 1 раз автопроверка
    useEffect(() => {
        if (!bookingModal?.id) return;
        if (!bookingModal?.fromReturn) return;
        if (!awaitingPayment) return;
        if (!isOwnBooking) return;

        // отметим что пользователь уже ходил на оплату (чтобы кнопка появилась даже после закрытия)
        rememberPaymentStarted(bookingModal.id);

        // авто-проверка один раз
        if (!paymentUI.checkedOnce && !paymentUI.loading) {
            void handleCheckPaymentStatus(bookingModal.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingModal?.id, bookingModal?.fromReturn]);

    // ✅ redirect-flow на ЮKassa
    const handleProceedToPayment = async () => {
        if (!bookingModal?.id) return;
        if (!isOwnBooking) {
            toast.info("Оплатить может только владелец брони");
            return;
        }

        paymentStartedRef.current = true;
        autoCancelOnceRef.current = true;

        setSaving(true);
        try {
            const returnUrl = buildPaymentReturnUrl(bookingModal.id);

            const res = await sessionsApi.startPayment(bookingModal.id, { returnUrl });

            const confirmationUrl =
                res?.confirmationUrl || res?.confirmation_url || res?.url || null;

            if (!confirmationUrl) throw new Error("Не удалось получить ссылку для оплаты");

            // ✅ запоминаем "пользователь реально уходил в оплату"
            rememberPaymentStarted(bookingModal.id);

            window.location.assign(String(confirmationUrl));
        } catch (e) {
            console.error(e);
            paymentStartedRef.current = false;
            autoCancelOnceRef.current = false;
            toast.error(e?.message || "Не удалось начать оплату. Попробуйте снова.");
        } finally {
            setSaving(false);
        }
    };

    const handleApplyCode = async () => {
        if (!bookingModal?.id) return;
        if (!isOwnBooking) {
            toast.info("Применить код может только владелец брони");
            return;
        }

        const code = String(paymentCode || "").trim();
        if (!code) {
            toast.info("Введите код сертификата или промокода");
            return;
        }

        paymentStartedRef.current = true;
        autoCancelOnceRef.current = true;

        setSaving(true);
        try {
            const returnUrl = buildPaymentReturnUrl(bookingModal.id);

            const discountPayload =
                paymentCodeType === "CERTIFICATE"
                    ? { certificateCode: code }
                    : { promoCode: code };

            const res = await sessionsApi.startPayment(bookingModal.id, {
                returnUrl,
                ...discountPayload,
            });

            const confirmationUrl =
                res?.confirmationUrl || res?.confirmation_url || res?.url || null;

            // Если после применения кода ещё осталась сумма к оплате — редиректим на платёжку.
            if (confirmationUrl) {
                toast.success("Код применён, переходим к оплате…");
                rememberPaymentStarted(bookingModal.id);
                window.location.assign(String(confirmationUrl));
                return;
            }

            // Если редиректа нет — считаем, что код применился без внешней оплаты.
            toast.success("Код успешно применён");
            await load();
            await handleCheckPaymentStatus(bookingModal.id);
        } catch (e) {
            console.error(e);
            paymentStartedRef.current = false;
            autoCancelOnceRef.current = false;
            toast.error(e?.message || "Не удалось применить код. Проверьте и попробуйте снова.");
        } finally {
            setSaving(false);
        }
    };


    const deleteQuickSlotBlock = async () => {
        if (!quickSlotModal || !psyId || !canManage) return;

        const existingBreakId = quickSlotModal.existingBreakId || null;
        if (existingBreakId) {
            setSavingQuickSlot(true);
            try {
                await sessionsApi.deleteWorkBreak(psyId, existingBreakId);
                toast.success(quickSlotModal.type === QUICK_SLOT_TYPES.BUSY ? "Занятость удалена" : "Перерыв удалён");
                setQuickSlotModal(null);
                await load();
            } catch (err) {
                toast.error(err?.message || "Не удалось удалить блокировку слота");
            } finally {
                setSavingQuickSlot(false);
            }
            return;
        }

        const parsedDate =
            parseDateInputToYmd(quickSlotDateInput) ||
            parseDateInputToYmd(quickSlotModal.date) ||
            null;
        const parsedTime =
            parseTimeInputToHm(quickSlotTimeInput) ||
            parseTimeInputToHm(quickSlotModal.time) ||
            null;
        const date = String(parsedDate || "").trim();
        const time = roundTimeToStep(String(parsedTime || "10:00"), 5);
        const duration = Math.max(15, Number(quickSlotModal.duration) || quickDurationBase);
        const endTime = addMinutesToHm(time, duration);

        if (!parsedDate || !parsedTime) {
            toast.error("Укажите корректную дату и время.");
            return;
        }

        setSavingQuickSlot(true);
        try {
            await sessionsApi.upsertWorkBreak(psyId, {
                date,
                startTime: time,
                endTime,
                type: QUICK_SLOT_TYPES.BUSY,
                kind: QUICK_SLOT_TYPES.BUSY,
                note: quickSlotModal.note || "Слот удалён вручную",
            });
            toast.success("Слот убран из доступных");
            setQuickSlotModal(null);
            await load();
        } catch (err) {
            toast.error(err?.message || "Не удалось удалить слот");
        } finally {
            setSavingQuickSlot(false);
        }
    };

    const saveQuickSlot = async () => {
        if (!quickSlotModal || !psyId) return;
        if (!canManage) {
            toast.info("Добавлять слоты может только психолог или администратор.");
            return;
        }

        const parsedDate =
            parseDateInputToYmd(quickSlotDateInput) ||
            parseDateInputToYmd(quickSlotModal.date) ||
            null;
        const parsedTime =
            parseTimeInputToHm(quickSlotTimeInput) ||
            parseTimeInputToHm(quickSlotModal.time) ||
            null;
        const date = String(parsedDate || "").trim();
        const time = roundTimeToStep(String(parsedTime || "10:00"), 5);
        const rawDuration = Math.max(
            15,
            Number(quickSlotModal.duration) || quickDurationBase
        );
        const type = quickSlotModal.type || QUICK_SLOT_TYPES.FREE;
        const duration =
            type === QUICK_SLOT_TYPES.FREE
                ? Math.max(rawDuration, quickDurationBase)
                : rawDuration;
        const endTime = addMinutesToHm(time, duration);
        const startMins = parseHmToMinutes(time);
        const endMins = parseHmToMinutes(endTime);
        const note = String(quickSlotModal.note || "").trim();

        if (!parsedDate || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            toast.error("Укажите корректную дату.");
            return;
        }

        if (type !== QUICK_SLOT_TYPES.DAY_OFF) {
            if (!parsedTime || !/^([01]\d|2[0-3]):[0-5]\d$/.test(time)) {
                toast.error("Укажите корректное время начала.");
                return;
            }
            if (endMins <= startMins || endMins > 24 * 60) {
                toast.error("Проверьте время и длительность слота.");
                return;
            }
        }

        setSavingQuickSlot(true);
        try {
            if (type === QUICK_SLOT_TYPES.DAY_OFF) {
                await sessionsApi.createDayOff(psyId, {
                    date,
                    reason: note || undefined,
                });
                toast.success("Выходной добавлен");
                setQuickSlotModal(null);
                await load();
                return;
            }

            if (type === QUICK_SLOT_TYPES.BREAK || type === QUICK_SLOT_TYPES.BUSY) {
                await sessionsApi.upsertWorkBreak(psyId, {
                    id: quickSlotModal.existingBreakId || null,
                    date,
                    startTime: time,
                    endTime,
                    type,
                    kind: type,
                    note: note || undefined,
                    reason: note || undefined,
                });
                toast.success(type === QUICK_SLOT_TYPES.BUSY ? "Слот помечен как занятый" : "Перерыв сохранён");
                setQuickSlotModal(null);
                await load();
                return;
            }

            if (!canInlineScheduleManage) {
                toast.info("Быстрое добавление слотов доступно только в режиме редактирования расписания.");
                return;
            }

            if ((dayOffs || []).some((entry) => String(entry?.date) === date)) {
                toast.info("На эту дату уже назначен выходной. Сначала удалите его.");
                return;
            }

            const base = ensureFlexibleShape(
                sidebarFlexible || effectiveFlexibleSchedule,
                quickDurationBase
            );
            const next = cloneDeep(base);
            const weekDate = mondayByYmd(date);
            const dayKey = dayKeyByYmd(date);
            let weekEntry = next.flexible.find(
                (entry) => String(entry?.dateFrom) === weekDate
            );

            if (!weekEntry) {
                // Важно: если психолог добавляет один ручной слот поверх фиксированного графика,
                // не создаём пустую гибкую неделю. Иначе гибкий график заменит всю неделю
                // и визуально "пропадут" остальные записи/слоты.
                weekEntry = {
                    dateFrom: weekDate,
                    week: cloneScheduleWeekForFlexible(sidebarSchedule || effectiveSchedule),
                };
                next.flexible.push(weekEntry);
            }

            weekEntry.week = {
                ...buildEmptyWeekShape(),
                ...(weekEntry?.week && typeof weekEntry.week === "object"
                    ? weekEntry.week
                    : {}),
            };

            const currentIntervals = Array.isArray(weekEntry.week?.[dayKey])
                ? weekEntry.week[dayKey]
                : [];
            weekEntry.week[dayKey] = normalizeIntervalsForDay([
                ...currentIntervals,
                { start: time, end: endTime },
            ]);

            next.flexible = [...next.flexible].sort((a, b) =>
                String(a?.dateFrom || "").localeCompare(String(b?.dateFrom || ""))
            );

            const saved = await sessionsApi.saveFlexibleSchedule(psyId, next);
            const normalized =
                Array.isArray(saved?.flexible) && saved.flexible.length ? saved : null;
            setFlexibleSchedule(normalized);
            setFlexibleDraft(cloneDeep(saved || next));
            setScheduleMode("FLEXIBLE");

            toast.success("Слот добавлен в гибкий график");
            setQuickSlotModal(null);
            await load();
        } catch (err) {
            toast.error(err?.message || "Не удалось сохранить слот");
        } finally {
            setSavingQuickSlot(false);
        }
    };

    const onDateClick = (arg) => {
        if (canManage) {
            const clickDate = arg?.date || new Date();
            const date = dateToYmd(clickDate);
            const time = arg?.allDay ? "10:00" : toQuickTime(clickDate, "10:00");
            setQuickSlotModal({
                date,
                time,
                duration: quickDurationBase,
                type: QUICK_SLOT_TYPES.FREE,
                note: "",
                sourceKind: "DATE",
            });
            return;
        }

        if (!canManageDayOff) return;
        const date = dateToYmd(arg.date);
        const existing = dayOffs.find((x) => x.date === date);
        const resolvedId =
            existing?.id ??
            existing?.dayOffId ??
            existing?.dayoffId ??
            existing?.day_off_id ??
            existing?.day_offId ??
            existing?.dayoff_id ??
            null;
        setDayOffModal({
            date,
            reason: existing?.reason || "",
            // Even if id is null (backend can return id:null), we still treat it as existing by date.
            existingId: resolvedId,
            exists: !!existing,
        });
    };

    const saveDayOff = async () => {
        if (!dayOffModal || !psyId) return;

        setSaving(true);
        try {
            await sessionsApi.createDayOff(psyId, {
                date: dayOffModal.date,
                reason: dayOffModal.reason,
            });
            await load();
            setDayOffModal(null);
            toast.success("Выходной сохранён");
        } catch (e) {
            toast.error(e?.message || "Не удалось сохранить выходной");
        } finally {
            setSaving(false);
        }
    };

    const deleteDayOff = async () => {
        if (!psyId) return;
        // backend may return existing day-off with id:null, so allow delete by date
        if (!dayOffModal?.exists) return;

        setSaving(true);
        try {
            await sessionsApi.deleteDayOff(psyId, {
                id: dayOffModal.existingId,
                date: dayOffModal.date,
            });
            await load();
            setDayOffModal(null);
            toast.success("Выходной удалён");
        } catch (e) {
            toast.error(e?.message || "Не удалось удалить выходной");
        } finally {
            setSaving(false);
        }
    };

    const pretty = (d) => {
        if (!d) return "";
        const dt = new Date(d);
        return dt.toLocaleString("ru-RU", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    if (!psyId) {
        return (
            <div className="b-calendar__empty">
                <h3>Выберите психолога</h3>
                <p>Чтобы показать свободные слоты, сначала выберите специалиста.</p>
            </div>
        );
    }

    const todayYmd = dateToYmd(new Date());

    return (
        <div className={`b-calendar b-calendar--neo ${loading ? "is-loading" : ""}`} data-onboarding-target="calendar-page">
            <button
                type="button"
                className={`b-calendar-neo__overlay ${sidebarOpen ? "is-open" : ""}`}
                onClick={() => setSidebarOpen(false)}
                aria-label="Закрыть панель расписания"
            />

            <div className="b-calendar-neo">
                <aside className={`b-calendar-neo__sidebar ${sidebarOpen ? "is-open" : ""}`}>
                    <div className="b-calendar-neo__side-head">
                        <div className="b-calendar-neo__side-title">Расписание</div>
                        <button
                            type="button"
                            className="b-calendar-neo__side-close"
                            onClick={() => setSidebarOpen(false)}
                            aria-label="Закрыть"
                        >
                            ✕
                        </button>
                    </div>

                    <section className="b-calendar-neo__section">
                        <div className="b-calendar-neo__section-label">Мини-календарь</div>
                        <div className="b-mini-cal">
                            <div className="b-mini-cal__head">
                                <span className="b-mini-cal__title">{miniMonthLabel}</span>
                                <div className="b-mini-cal__nav">
                                    <button
                                        type="button"
                                        className="b-mini-cal__nav-btn"
                                        onClick={() => handleMiniMonthShift(-1)}
                                        aria-label="Предыдущий месяц"
                                    >
                                        ‹
                                    </button>
                                    <button
                                        type="button"
                                        className="b-mini-cal__nav-btn"
                                        onClick={() => handleMiniMonthShift(1)}
                                        aria-label="Следующий месяц"
                                    >
                                        ›
                                    </button>
                                </div>
                            </div>
                            <div className="b-mini-cal__weekdays">
                                {MINI_WEEKDAYS.map((label) => (
                                    <span key={label}>{label}</span>
                                ))}
                            </div>
                            <div className="b-mini-cal__grid">
                                {miniGridDays.map((item) => {
                                    const cls = [
                                        "b-mini-cal__day",
                                        item.isOtherMonth ? "is-other" : "",
                                        markedDates.has(item.ymd) ? "has-event" : "",
                                        item.ymd === todayYmd ? "is-today" : "",
                                        item.ymd === selectedMiniYmd ? "is-selected" : "",
                                    ]
                                        .filter(Boolean)
                                        .join(" ");

                                    return (
                                        <button
                                            key={item.ymd}
                                            type="button"
                                            className={cls}
                                            onClick={() => handleMiniDayClick(item.date)}
                                        >
                                            {item.dayNum}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </section>

                    <section className="b-calendar-neo__section" data-onboarding-target="calendar-working-hours" data-tour="calendar-working-hours">
                        <div className="b-calendar-neo__section-label">График работы</div>
                        {canInlineScheduleManage ? (
                            <>
                                <div className="b-calendar-neo__mode-toggle">
                                    <button
                                        type="button"
                                        className={`b-calendar-neo__mode-btn ${scheduleMode === "FIXED" ? "is-active" : ""}`}
                                        onClick={() => setScheduleMode("FIXED")}
                                    >
                                        Фиксированный
                                    </button>
                                    <button
                                        type="button"
                                        className={`b-calendar-neo__mode-btn ${scheduleMode === "FLEXIBLE" ? "is-active" : ""}`}
                                        onClick={() => {
                                            setScheduleMode("FLEXIBLE");
                                            setFlexibleDraft((prev) => {
                                                if (prev) return prev;
                                                return {
                                                    slotMinutes:
                                                        Number(sidebarSchedule?.slotMinutes) || 50,
                                                    flexible: [],
                                                };
                                            });
                                        }}
                                    >
                                        Гибкий
                                    </button>
                                </div>

                                {scheduleMode === "FLEXIBLE" ? (
                                    <>
                                        <div className="b-calendar-neo__hint">
                                            Задайте разные недели вручную: календарь будет использовать их вместо фиксированного шаблона.
                                        </div>
                                        <div className="b-calendar-neo__flex-summary">
                                            Недель с индивидуальным графиком: {flexibleWeeks.length}
                                        </div>
                                        {quickFlexibleRows.length ? (
                                            <div className="b-calendar-neo__flex-days">
                                                {quickFlexibleRows.map((row) => (
                                                    <div className="b-calendar-neo__flex-day" key={row.ymd}>
                                                        <div className="b-calendar-neo__flex-day-head">
                                                            <span>{row.label}</span>
                                                            <button
                                                                type="button"
                                                                className="b-calendar-neo__flex-add-btn"
                                                                onClick={() =>
                                                                    openQuickSlotModal({
                                                                        date: row.ymd,
                                                                        type: QUICK_SLOT_TYPES.FREE,
                                                                    })
                                                                }
                                                            >
                                                                + слот
                                                            </button>
                                                        </div>
                                                        {row.slots.length ? (
                                                            <div className="b-calendar-neo__flex-slot-list">
                                                                {row.slots.slice(0, 4).map((slot) => (
                                                                    <span key={`${row.ymd}-${slot}`}>{slot}</span>
                                                                ))}
                                                                {row.slots.length > 4 ? (
                                                                    <span>+{row.slots.length - 4}</span>
                                                                ) : null}
                                                            </div>
                                                        ) : (
                                                            <div className="b-calendar-neo__flex-empty">Выходной</div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null}
                                        {flexibleWeeks.length ? (
                                            <div className="b-calendar-neo__flex-weeks">
                                                {flexibleWeeks.slice(0, 4).map((entry) => (
                                                    <div className="b-calendar-neo__flex-week" key={entry.dateFrom}>
                                                        {formatFlexibleWeekLabel(entry.dateFrom)}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="b-calendar-neo__hint">
                                                Пока нет заданных недель. Откройте редактор и добавьте первую.
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <label className="b-calendar-neo__slot-field">
                                            <span>Минут на сессию</span>
                                            <input
                                                type="number"
                                                min={15}
                                                step={5}
                                                value={Number(sidebarSchedule?.slotMinutes) || 50}
                                                onChange={(e) => updateSlotMinutesInDraft(e.target.value)}
                                            />
                                        </label>
                                        {weekScheduleRows.map((row) => (
                                            <div
                                                key={row.dayKey}
                                                className={`b-day-row ${row.enabled ? "is-on" : ""}`}
                                            >
                                                <div className="b-day-row__left">
                                                    <div className="b-day-row__name">{row.title}</div>
                                                    <div className="b-day-row__sub">{row.subtitle}</div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={`b-day-row__switch ${row.enabled ? "is-on" : ""}`}
                                                    aria-label={`${row.enabled ? "Отключить" : "Включить"} ${row.title}`}
                                                    onClick={() => toggleDayInScheduleDraft(row.dayKey)}
                                                />
                                            </div>
                                        ))}
                                    </>
                                )}

                                <div className="b-calendar-neo__schedule-actions">
                                    <button
                                        type="button"
                                        className="b-calendar-neo__small-btn"
                                        onClick={() => setScheduleEditorOpen(true)}
                                    >
                                        Редактировать
                                    </button>
                                    <button
                                        type="button"
                                        className="b-calendar-neo__small-btn is-primary"
                                        onClick={saveCurrentScheduleMode}
                                        disabled={savingScheduleConfig}
                                    >
                                        {savingScheduleConfig ? "Сохраняем..." : "Сохранить"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                {hasFlexibleSchedule ? (
                                    <div className="b-calendar-neo__hint">
                                        Включен гибкий график: слоты задаются на конкретные недели.
                                    </div>
                                ) : null}
                                {weekScheduleRows.map((row) => (
                                    <div
                                        key={row.dayKey}
                                        className={`b-day-row ${row.enabled ? "is-on" : ""}`}
                                    >
                                        <div className="b-day-row__left">
                                            <div className="b-day-row__name">{row.title}</div>
                                            <div className="b-day-row__sub">{row.subtitle}</div>
                                        </div>
                                        <div className={`b-day-row__switch ${row.enabled ? "is-on" : ""}`} />
                                    </div>
                                ))}
                            </>
                        )}
                    </section>

                    <section className="b-calendar-neo__section">
                        <div className="b-calendar-neo__section-label">
                            {upcomingSectionTitle}
                        </div>
                        {upcomingItems.length ? (
                            <div className="b-upcoming">
                                {upcomingItems.map((item) => (
                                    <div key={item.id} className="b-upcoming__item">
                                        <div className="b-upcoming__avatar">
                                            {initialsFromName(item.title)}
                                        </div>
                                        <div className="b-upcoming__info">
                                            <div className="b-upcoming__name">{item.title}</div>
                                            <div className="b-upcoming__time">{item.time}</div>
                                        </div>
                                        <div className={`b-upcoming__badge ${item.badgeClass}`}>
                                            {item.badge}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="b-calendar-neo__hint">Пока нет ближайших записей.</div>
                        )}
                    </section>
                </aside>

                <div className="b-calendar-neo__main">
                    <div className="b-calendar-neo__toolbar">
                        <div className="b-calendar-neo__toolbar-left">
                            <button
                                type="button"
                                className="b-calendar-neo__icon-btn"
                                onClick={handleCalendarPrev}
                                aria-label="Предыдущий период"
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                className="b-calendar-neo__icon-btn"
                                onClick={handleCalendarNext}
                                aria-label="Следующий период"
                            >
                                ›
                            </button>
                            <button
                                type="button"
                                className="b-calendar-neo__today-btn"
                                onClick={handleCalendarToday}
                            >
                                Сегодня
                            </button>
                            <button
                                type="button"
                                className="b-calendar-neo__schedule-btn"
                                onClick={() => setSidebarOpen(true)}
                            >
                                Расписание
                            </button>
                            <div className="b-calendar-neo__title-wrap">
                                <div className="b-calendar-neo__title">
                                    {calendarLabel || "Календарь сессий"}
                                </div>
                                <div className="b-calendar-neo__subtitle">
                                    {psychologist?.name || `Психолог #${psyId}`}
                                    {mode === "PSYCHO" ? " · настройка графика" : " · запись"}
                                </div>
                            </div>
                        </div>

                        <div className="b-calendar-neo__toolbar-right">
                            <div className="b-calendar-neo__view-toggle">
                                <button
                                    type="button"
                                    className={`b-calendar-neo__view-btn ${viewType === "timeGridDay" ? "is-active" : ""}`}
                                    onClick={() => handleCalendarView("timeGridDay")}
                                >
                                    День
                                </button>
                                <button
                                    type="button"
                                    className={`b-calendar-neo__view-btn ${viewType === "timeGridWeek" ? "is-active" : ""}`}
                                    onClick={() => handleCalendarView("timeGridWeek")}
                                >
                                    Неделя
                                </button>
                                <button
                                    type="button"
                                    className={`b-calendar-neo__view-btn ${viewType === "dayGridMonth" ? "is-active" : ""}`}
                                    onClick={() => handleCalendarView("dayGridMonth")}
                                >
                                    Месяц
                                </button>
                            </div>
                            {mode === "CLIENT" ? (
                                <button
                                    type="button"
                                    className="b-calendar-neo__action-btn"
                                    onClick={handlePrimaryAction}
                                >
                                    Записаться
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="b-calendar-neo__add-btn"
                                    data-onboarding-target="calendar-create-session"
                                    data-tour="calendar-create-session"
                                    onClick={handlePrimaryAction}
                                >
                                    <svg viewBox="0 0 24 24" aria-hidden="true">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Записать
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="b-calendar-neo__legend">
                        <span className="b-badge b-badge--slot">Свободно</span>
                        <span className="b-badge b-badge--slot-locked">Запись закрыта</span>
                        <span className="b-badge b-badge--booking">Занято</span>
                        <span className="b-badge b-badge--break">Перерыв</span>
                        <span className="b-badge b-badge--dayoff">Выходной</span>
                    </div>

                    <div className="b-calendar-neo__fc-wrap">
                        <FullCalendar
                            ref={calendarRef}
                            className="b-calendar-neo__fc"
                            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                            initialView="timeGridWeek"
                            locale={ruLocale}
                            firstDay={1}
                            height="auto"
                            nowIndicator
                            selectable={false}
                            slotMinTime="07:00:00"
                            slotMaxTime="23:00:00"
                            slotDuration="01:00:00"
                            slotLabelInterval="01:00:00"
                            allDaySlot={false}
                            headerToolbar={false}
                            datesSet={onDatesSet}
                            events={events}
                            eventClick={onEventClick}
                            dateClick={onDateClick}
                            eventTimeFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
                            displayEventEnd
                        />
                    </div>
                </div>
            </div>

            <Modal
                open={scheduleEditorOpen}
                title={scheduleMode === "FLEXIBLE" ? "Гибкий график" : "Фиксированный график"}
                onClose={() => (savingScheduleConfig ? null : setScheduleEditorOpen(false))}
            >
                {scheduleMode === "FLEXIBLE" ? (
                    <FlexibleScheduleEditor
                        value={
                            flexibleDraft || {
                                slotMinutes: Number(sidebarSchedule?.slotMinutes) || 50,
                                flexible: [],
                            }
                        }
                        onChange={setFlexibleDraft}
                        onSave={async (next) => {
                            setFlexibleDraft(next);
                            await saveFlexibleSchedule(next);
                        }}
                        saving={savingScheduleConfig}
                    />
                ) : (
                    <WeeklyScheduleEditor
                        value={sidebarSchedule || ensureScheduleShape(null)}
                        onChange={setScheduleDraft}
                        onSave={async (next) => {
                            setScheduleDraft(next);
                            await saveFixedSchedule(next);
                        }}
                        saving={savingScheduleConfig}
                        showBuffer={false}
                    />
                )}
            </Modal>

            <Modal
                open={!!quickSlotModal}
                title={quickSlotModal?.existingBreakId ? "Редактировать блокировку" : quickSlotModal?.sourceKind === "SLOT" ? "Настройка слота" : "Новая запись"}
                onClose={() => (savingQuickSlot ? null : setQuickSlotModal(null))}
                actions={
                    <>
                        {(quickSlotModal?.existingBreakId || quickSlotModal?.sourceKind === "SLOT") ? (
                            <button
                                className="b-btn b-btn--danger-soft"
                                onClick={deleteQuickSlotBlock}
                                disabled={savingQuickSlot}
                            >
                                {quickSlotModal?.existingBreakId ? "Удалить блокировку" : "Удалить слот"}
                            </button>
                        ) : null}
                        <button
                            className="b-btn b-btn--transparent"
                            onClick={() => setQuickSlotModal(null)}
                            disabled={savingQuickSlot}
                        >
                            Отмена
                        </button>
                        <button
                            className="b-btn b-btn--primary"
                            onClick={saveQuickSlot}
                            disabled={savingQuickSlot}
                        >
                            {savingQuickSlot ? "Сохраняем..." : "Сохранить"}
                        </button>
                    </>
                }
            >
                {quickSlotModal ? (
                    <div className="b-quick-slot">
                        <div className="b-quick-slot__group">
                            <div className="b-quick-slot__label">Тип</div>
                            <div className="b-quick-slot__chips">
                                <button
                                    type="button"
                                    className={`b-quick-slot__chip ${
                                        quickSlotModal.type === QUICK_SLOT_TYPES.FREE
                                            ? "is-free"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setQuickSlotModal((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      type: QUICK_SLOT_TYPES.FREE,
                                                      duration: Math.max(
                                                          quickDurationBase,
                                                          Number(prev.duration) || quickDurationBase
                                                      ),
                                                  }
                                                : prev
                                        )
                                    }
                                    disabled={savingQuickSlot}
                                >
                                    Свободно
                                </button>
                                <button
                                    type="button"
                                    className={`b-quick-slot__chip ${
                                        quickSlotModal.type === QUICK_SLOT_TYPES.BREAK
                                            ? "is-break"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setQuickSlotModal((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      type: QUICK_SLOT_TYPES.BREAK,
                                                  }
                                                : prev
                                        )
                                    }
                                    disabled={savingQuickSlot}
                                >
                                    Перерыв
                                </button>

                                <button
                                    type="button"
                                    className={`b-quick-slot__chip ${
                                        quickSlotModal.type === QUICK_SLOT_TYPES.BUSY
                                            ? "is-busy"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setQuickSlotModal((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      type: QUICK_SLOT_TYPES.BUSY,
                                                  }
                                                : prev
                                        )
                                    }
                                    disabled={savingQuickSlot}
                                >
                                    Занято
                                </button>
                                <button
                                    type="button"
                                    className={`b-quick-slot__chip ${
                                        quickSlotModal.type === QUICK_SLOT_TYPES.DAY_OFF
                                            ? "is-dayoff"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setQuickSlotModal((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      type: QUICK_SLOT_TYPES.DAY_OFF,
                                                  }
                                                : prev
                                        )
                                    }
                                    disabled={savingQuickSlot}
                                >
                                    Выходной
                                </button>
                            </div>
                        </div>

                        <div className="b-quick-slot__row">
                            <label className="b-quick-slot__field">
                                <span>Дата</span>
                                <input
                                    className="b-input"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="дд.мм.гггг"
                                    value={quickSlotDateInput}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        setQuickSlotDateInput(raw);
                                        const parsed = parseDateInputToYmd(raw);
                                        if (!parsed) return;
                                        setQuickSlotModal((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      date: parsed,
                                                  }
                                                : prev
                                        );
                                    }}
                                    onBlur={() => {
                                        const parsed = parseDateInputToYmd(quickSlotDateInput);
                                        if (parsed) {
                                            setQuickSlotModal((prev) =>
                                                prev
                                                    ? {
                                                          ...prev,
                                                          date: parsed,
                                                      }
                                                    : prev
                                            );
                                            setQuickSlotDateInput(formatYmdAsRuDots(parsed));
                                            return;
                                        }
                                        setQuickSlotDateInput(formatYmdAsRuDots(quickSlotModal.date));
                                    }}
                                    disabled={savingQuickSlot}
                                />
                            </label>
                            <label className="b-quick-slot__field">
                                <span>Время</span>
                                <input
                                    className="b-input"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="чч:мм"
                                    value={quickSlotTimeInput}
                                    onChange={(e) => {
                                        const raw = e.target.value;
                                        setQuickSlotTimeInput(raw);
                                        const parsed = parseTimeInputToHm(raw);
                                        if (!parsed) return;
                                        setQuickSlotModal((prev) =>
                                            prev
                                                ? {
                                                      ...prev,
                                                      time: parsed,
                                                  }
                                                : prev
                                        );
                                    }}
                                    onBlur={() => {
                                        const parsed = parseTimeInputToHm(quickSlotTimeInput);
                                        if (parsed) {
                                            setQuickSlotModal((prev) =>
                                                prev
                                                    ? {
                                                          ...prev,
                                                          time: parsed,
                                                      }
                                                    : prev
                                            );
                                            setQuickSlotTimeInput(parsed);
                                            return;
                                        }
                                        setQuickSlotTimeInput(String(quickSlotModal.time || ""));
                                    }}
                                    disabled={
                                        savingQuickSlot ||
                                        quickSlotModal.type === QUICK_SLOT_TYPES.DAY_OFF
                                    }
                                />
                            </label>
                        </div>

                        <label className="b-quick-slot__field">
                            <span>Длительность</span>
                            <select
                                className="b-input"
                                value={Number(quickSlotModal.duration) || quickDurationBase}
                                onChange={(e) =>
                                    setQuickSlotModal((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  duration: Math.max(15, Number(e.target.value) || quickDurationBase),
                                              }
                                            : prev
                                    )
                                }
                                disabled={
                                    savingQuickSlot ||
                                    quickSlotModal.type === QUICK_SLOT_TYPES.DAY_OFF
                                }
                            >
                                {quickDurationOptions
                                    .filter((mins) =>
                                        quickSlotModal.type === QUICK_SLOT_TYPES.FREE
                                            ? mins >= quickDurationBase
                                            : true
                                    )
                                    .map((mins) => (
                                    <option key={mins} value={mins}>
                                        {mins} минут
                                    </option>
                                    ))}
                            </select>
                        </label>

                        <label className="b-quick-slot__field">
                            <span>Заметка</span>
                            <input
                                className="b-input"
                                type="text"
                                value={quickSlotModal.note}
                                onChange={(e) =>
                                    setQuickSlotModal((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  note: e.target.value,
                                              }
                                            : prev
                                    )
                                }
                                placeholder={
                                    quickSlotModal.type === QUICK_SLOT_TYPES.DAY_OFF
                                        ? "Причина выходного"
                                        : quickSlotModal.type === QUICK_SLOT_TYPES.BREAK
                                            ? "Комментарий к перерыву"
                                            : quickSlotModal.type === QUICK_SLOT_TYPES.BUSY
                                                ? "Например: внешняя запись"
                                                : "Опционально"
                                }
                                disabled={savingQuickSlot}
                            />
                        </label>

                        <div className="b-quick-slot__hint">
                            {quickSlotModal.type === QUICK_SLOT_TYPES.DAY_OFF
                                ? `Будет добавлен выходной: ${prettyDateRu(quickSlotModal.date)}.`
                                : quickSlotModal.type === QUICK_SLOT_TYPES.BUSY
                                    ? `Слот будет помечен как занятый: ${prettyDateRu(quickSlotModal.date)}, ${quickSlotModal.time} — ${addMinutesToHm(quickSlotModal.time, quickSlotModal.duration)}.`
                                    : `Будет добавлено: ${prettyDateRu(
                                      quickSlotModal.date
                                  )}, ${quickSlotModal.time} — ${addMinutesToHm(
                                      quickSlotModal.time,
                                      quickSlotModal.duration
                                  )}.`}
                        </div>
                    </div>
                ) : null}
            </Modal>

            {/* Модалка оплаты / деталей брони */}
            <Modal
                open={!!bookingModal}
                title={awaitingPayment ? "Ожидает оплаты" : "Детали сессии"}
                className="b-modal--booking-state"
                cardClassName="b-modal__card--booking-state"
                onClose={() => {
                    if (saving) return;
                    setBookingModal(null); // ✅ закрытие НЕ отменяет бронь
                }}
                actions={
                    awaitingPayment ? (
                        <>
                            {isOwnBooking ? (
                                <>
                                    <button
                                        className="b-btn b-btn--transparent"
                                        onClick={handleCancelBooking}
                                        disabled={saving}
                                    >
                                        Отменить бронь
                                    </button>

                                    {/* ✅ ВАЖНО: показываем "Проверить статус" ТОЛЬКО если пользователь реально ходил в оплату */}
                                    {showCheckStatusBtn && (
                                        <button
                                            className="b-btn b-btn--transparent"
                                            onClick={() => handleCheckPaymentStatus(bookingModal.id)}
                                            disabled={saving || paymentUI.loading}
                                        >
                                            {paymentUI.loading ? "Проверяем..." : "Проверить статус"}
                                        </button>
                                    )}

                                    <button
                                        className="b-btn b-btn--transparent"
                                        onClick={handleApplyCode}
                                        disabled={saving || !canPayThisBooking}
                                        title={!canPayThisBooking ? "Применить код может только владелец брони" : ""}
                                    >
                                        Применить код
                                    </button>

                                    <button
                                        className="b-btn b-btn--primary"
                                        onClick={handleProceedToPayment}
                                        disabled={saving || !canPayThisBooking}
                                        title={!canPayThisBooking ? "Оплатить может только владелец брони" : ""}
                                    >
                                        Оплатить
                                    </button>
                                </>
                            ) : (
                                <button
                                    className="b-btn b-btn--transparent"
                                    onClick={() => setBookingModal(null)}
                                    disabled={saving}
                                >
                                    Закрыть
                                </button>
                            )}
                        </>
                    ) : (
                        <>
                            {canCancelPaidBooking ? (
                                <button
                                    className="b-btn b-btn--transparent"
                                    onClick={handleCancelPaidBooking}
                                    disabled={saving}
                                    title={!isFutureBooking ? "Нельзя отменить прошедшую сессию" : ""}
                                >
                                    Отменить сессию
                                </button>
                            ) : null}

                            {canRefundThisBooking ? (
                                <button
                                    className="b-btn b-btn--transparent"
                                    onClick={handleRefundBooking}
                                    disabled={saving || refunding}
                                    title={!canRefundThisBooking ? "Возврат доступен, если до начала больше 24 часов" : ""}
                                >
                                    {refunding ? "Оформляем..." : "Вернуть деньги"}
                                </button>
                            ) : null}

                            {!canCancelPaidBooking && !canRefundThisBooking ? (
                                <button
                                    className="b-btn b-btn--transparent"
                                    onClick={() => setBookingModal(null)}
                                    disabled={saving}
                                >
                                    Закрыть
                                </button>
                            ) : (
                                <button
                                    className="b-btn b-btn--transparent"
                                    onClick={() => setBookingModal(null)}
                                    disabled={saving || refunding}
                                >
                                    Закрыть
                                </button>
                            )}
                        </>
                    )
                }
            >
                {bookingModal && (
                    <div className="b-modal__grid">
                        <div className="b-modal__row">
                            <div className="b-modal__label">Время</div>
                            <div className="b-modal__value">
                                {pretty(bookingModal.startDateTime)} —{" "}
                                {new Date(bookingModal.endDateTime).toLocaleTimeString("ru-RU", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                })}
                            </div>
                        </div>

                        <div className="b-modal__row">
                            <div className="b-modal__label">Статус</div>
                            <div className="b-modal__value">
                                {awaitingPayment ? "Ожидает оплаты" : prettyBookingStatus(bookingModal.status)}
                            </div>
                        </div>

                        {bookingModal.priceAtTime != null && (
                            <div className="b-modal__row">
                                <div className="b-modal__label">Сумма</div>
                                <div className="b-modal__value">{bookingModal.priceAtTime} ₽</div>
                            </div>
                        )}

                        {awaitingPayment && (
                            <>
                                <div className="b-modal__hint warning">
                                    <b>Перед оплатой:</b> перенос или отмена консультации возможны не позднее чем за 1 день до её проведения.
                                    После оплаты вы вернётесь на сайт, а статус брони обновится автоматически.
                                    Если подтверждение не появилось сразу — подождите несколько секунд и обновите страницу.
                                </div>

                                {bookingModal.holdExpiresAt ? (
                                    <div className="b-modal__hint">
                                        Завершите оплату в течение {HOLD_PAYMENT_MIN} минут, иначе бронь будет отменена.{" "}
                                        <b>Осталось: {formatMMSS(holdLeftMs)}</b>
                                    </div>
                                ) : (
                                    <div className="b-modal__hint">
                                        Бронь удерживается ограниченное время. Если оплата не выполнена — бронь будет снята автоматически.
                                    </div>
                                )}

                                {paymentUI?.message ? (
                                    <div className={`b-modal__hint ${paymentUI.kind || ""}`}>
                                        {paymentUI.message}
                                    </div>
                                ) : null}

                                {isOwnBooking && (
                                    <div className="b-modal__hint" style={{ marginTop: 4 }}>
                                        <div style={{ display: "grid", gap: 8 }}>
                                            <select
                                                className="b-input"
                                                value={paymentCodeType}
                                                onChange={(e) =>
                                                    setPaymentCodeType(
                                                        e.target.value === "CERTIFICATE" ? "CERTIFICATE" : "PROMO"
                                                    )
                                                }
                                                disabled={saving}
                                            >
                                                <option value="PROMO">Промокод</option>
                                                <option value="CERTIFICATE">Сертификат</option>
                                            </select>
                                            <input
                                                className="b-input"
                                                value={paymentCode}
                                                onChange={(e) => setPaymentCode(e.target.value)}
                                                placeholder={
                                                    paymentCodeType === "CERTIFICATE"
                                                        ? "Введите код сертификата"
                                                        : "Введите промокод"
                                                }
                                                disabled={saving}
                                            />
                                        </div>
                                    </div>
                                )}

                                {!isOwnBooking && (
                                    <div className="b-modal__hint">
                                        Эта бронь принадлежит другому клиенту. Оплата доступна только владельцу.
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </Modal>

            <AuthRequiredModal
                open={authRequiredOpen}
                onClose={() => setAuthRequiredOpen(false)}
                onLogin={() => {
                    setAuthRequiredOpen(false);
                    navigate("/login");
                }}
                title="Запись доступна после авторизации"
                message="Записаться на консультацию через календарь может только авторизованный пользователь."
                loginLabel="Перейти ко входу"
            />

            {/* Модалка выходного */}
            <Modal
                open={!!dayOffModal}
                title={dayOffModal?.exists ? "Редактировать выходной" : "Добавить выходной"}
                onClose={() => (saving ? null : setDayOffModal(null))}
                actions={
                    <>
                        {dayOffModal?.exists && (
                            <button
                                className="b-btn b-btn--transparent"
                                onClick={deleteDayOff}
                                disabled={saving}
                            >
                                Удалить
                            </button>
                        )}
                        <button className="b-btn" onClick={saveDayOff} disabled={saving}>
                            Сохранить
                        </button>
                    </>
                }
            >
                {dayOffModal && (
                    <div className="b-modal__grid">
                        <div className="b-modal__row">
                            <div className="b-modal__label">Дата</div>
                            <div className="b-modal__value">{dayOffModal.date}</div>
                        </div>
                        <div className="b-modal__row">
                            <div className="b-modal__label">Причина</div>
                            <div className="b-modal__value">
                                <input
                                    className="b-input"
                                    value={dayOffModal.reason}
                                    placeholder="Например: отпуск, болезнь, конференция"
                                    onChange={(e) =>
                                        setDayOffModal((s) => ({ ...s, reason: e.target.value }))
                                    }
                                />
                            </div>
                        </div>
                        <div className="b-modal__hint">
                            Кликните по дню в календаре, чтобы добавить или изменить выходной.
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
